import { useMemo, useState, useEffect, useCallback, useRef, type RefObject } from 'react';
import { Platform, ScrollView, Image, useWindowDimensions, Pressable, View, StyleProp, ViewStyle } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useQuery } from '@tanstack/react-query';
import { Check, ChevronDown, ChevronUp, X, EyeOff } from 'lucide-react-native';

import { Box, Text } from '@/components/ui/primitives';
import { Card, CardContent } from './ui/Card';
import { Tooltip } from './ui/Tooltip';
import { AppSelect } from '@/components/ui/AppSelect';
import { Skeleton } from './ui/Skeleton';
import { useAuth } from '../hooks/useAuth';
import { apiRequestJson } from '../lib/queryClient';
import { trpcClient, useTRPC } from '@/lib/trpc';
import { getCurrentTime } from '../utils/timeUtils';
import { compareScoreThenTiebreaker } from '@shared/ranking';
import { formatPropStatHeaderRows, formatPropStatLine as formatSharedPropStatLine } from '@shared/propDisplay';
import { getCurrentUserScoresScrollY } from '@/lib/scoresScroll';
import { filterPastAndActiveWeeks } from '@shared/weeks';
import { clientLogger } from '../lib/datadog';
import { colors, typography } from '@/lib/theme';
import {
  getBetPoints,
  getBetLiveState,
  inferBetSideFromSelection,
  isPickCorrect,
  resolveLegacyScoringSystem,
  resolveWinningSide,
  type BetSide,
} from '@/lib/scoring';

const transparentColor = 'transparent';
const stickyScoreHeaderStyle: ViewStyle = {
  backgroundColor: colors.ink.paper,
  position: 'sticky' as ViewStyle['position'],
  top: 0,
  zIndex: 5,
};
const stickyScoreFooterStyle: ViewStyle = {
  backgroundColor: colors.ink.paper,
  bottom: 0,
  position: 'sticky' as ViewStyle['position'],
  zIndex: 6,
};

type HorizontalScrollPane = 'header' | 'body' | 'top' | 'bottom';

function ScoresHorizontalScrollbar({
  contentWidth,
  onScroll,
  scrollRef,
  sticky,
  testID,
}: {
  contentWidth: number;
  onScroll: (event: any) => void;
  scrollRef: RefObject<ScrollView | null>;
  sticky: 'top' | 'bottom';
  testID: string;
}) {
  if (Platform.OS !== 'web') {
    return null;
  }

  return (
    <ScrollView
      ref={scrollRef}
      horizontal
      nestedScrollEnabled
      onScroll={onScroll}
      scrollEventThrottle={16}
      showsHorizontalScrollIndicator
      style={[
        styles.horizontalScrollbarTrack,
        sticky === 'bottom' && stickyScoreFooterStyle,
      ]}
      testID={testID}
    >
      <View style={[styles.horizontalScrollbarFill, { width: Math.max(contentWidth, 1) }]} />
    </ScrollView>
  );
}

interface Team {
  id: string;
  name: string;
  city: string;
  abbreviation: string;
  logoUrl?: string | null;
}

interface Game {
  id: string;
  homeTeam: Team;
  awayTeam: Team;
  gameTime: string;
  isCompleted: boolean | null;
  winnerId: string | null;
  homeOdds: string | null;
  awayOdds: string | null;
  homeScore?: number | null;
  awayScore?: number | null;
  status?: string | null;
}

interface Pick {
  id: string;
  userId: string;
  gameId: string;
  selectedTeamId: string;
  side?: string; // Add side to track selection for custom props
  leagueWeekBetId?: string;
  betType?: 'moneyline' | 'spread' | 'total' | 'total_tiebreaker' | 'custom_prop';
  details?: { tiebreakerGuess?: number } | null;
  isCorrect: boolean | null;
  pointsEarned: number;
  scoringConfigOverride?: {
    inclusion?: {
      weekly?: boolean;
      season?: boolean;
    };
  } | null;
}

const getSideFromPick = (pick: Pick, game: Game): string | null => {
  if (pick.side) return pick.side;
  if (!pick.selectedTeamId) return null;
  const side = inferBetSideFromSelection(game, pick.selectedTeamId);
  if (side) return side;
  // For multi-option custom props where teamId might store the side string
  return pick.selectedTeamId;
};

interface LeagueGameBetDefinition {
  id: string;
  leagueWeekBetId?: string | null;
  betType: 'moneyline' | 'spread' | 'total' | 'total_tiebreaker' | 'custom_prop';
  propDefinitionId?: string | null;
  oddsOne?: number | null;
  oddsTwo?: number | null;
  displayInfo?: Record<string, unknown> | null;
  isPropOfTheWeek?: boolean | null;
  scoringConfigOverride?: {
    inclusion?: {
      weekly?: boolean;
      season?: boolean;
    };
    propOfTheWeek?: boolean;
  } | null;
}

const isPropOfTheWeekBet = (bet: LeagueGameBetDefinition): boolean =>
  bet.isPropOfTheWeek === true || bet.scoringConfigOverride?.propOfTheWeek === true;

interface LeagueSeasonWeekOption {
  id: string;
  leagueWeekId: string;
  label: string;
  displayLabel: string;
  weekNumber: number | null;
  startsAt: string | null;
  endsAt: string | null;
}

interface LeagueSeasonWeeksPayload {
  resolvedSeasonId: string;
  weeks: LeagueSeasonWeekOption[];
}

interface LeagueWeekGamesPayload {
  leagueWeekId: string | null;
  resolvedSeasonId: string;
  legacy?: boolean;
  games: {
    game: Game;
    bets: LeagueGameBetDefinition[];
  }[];
}

interface PropDefinition {
  id: string;
  nflGameId: string | null;
  propType: string;
  statType: string | null;
  comparison: string | null;
  threshold: number | string | null;
  displayConfig: Record<string, unknown>;
  playerIds: string[];
  players: { id: string; fullName: string; teamAbbr: string; position: string }[];
}

interface PropEvaluation {
  propDefinitionId: string;
  status: string;
  leadingSide: string | null;
  currentValues?: unknown;
  resolvedWinningSide: string | null;
}

const toNumericRecord = (value: unknown): Record<string, number> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(value).filter((entry): entry is [string, number] => {
      const [, entryValue] = entry;
      return typeof entryValue === 'number' && Number.isFinite(entryValue);
    }),
  );
};

const buildBetKey = (gameId: string, bet: LeagueGameBetDefinition) => {
  if (bet.leagueWeekBetId) {
    return bet.leagueWeekBetId;
  }
  if (bet.id) {
    return bet.id;
  }
  return `${gameId}-${bet.betType}`;
};

const derivePickKey = (pick: Pick) => pick.leagueWeekBetId ?? `${pick.gameId}-${pick.betType ?? 'moneyline'}`;

const formatSpread = (value?: number | null) => {
  if (value === undefined || value === null) return '';
  return value > 0 ? `+${value}` : `${value}`;
};

const formatOdds = (value?: number | string | null) => {
  if (value === undefined || value === null || value === '') return '';
  const parsed = typeof value === 'number' ? value : Number.parseFloat(value);
  if (!Number.isFinite(parsed)) return '';
  return parsed > 0 ? `+${parsed}` : `${parsed}`;
};

const formatTotal = (value?: number | null) => {
  if (value === undefined || value === null) return '';
  return `${value}`;
};

const resolveCustomPropLabel = (bet: LeagueGameBetDefinition): string => {
  const label = typeof bet.displayInfo?.label === 'string' ? bet.displayInfo.label.trim() : '';
  return label || 'Custom Prop';
};

const resolveCustomPropSideLabel = (
  bet: LeagueGameBetDefinition,
  side: string,
): string => {
  const sideKey = side.trim();
  if (sideKey === 'push') {
    return 'Push';
  }
  // Check options array first for multi-side bets
  if (bet.displayInfo?.options && Array.isArray(bet.displayInfo.options)) {
    const option = bet.displayInfo.options.find((o: any) => String(o.side).trim() === sideKey);
    if (option && typeof option.label === 'string') {
      return option.label.trim();
    }
  }

  // Fallback to legacy sideLabelOne/Two
  if (sideKey === 'one') {
    return (typeof bet.displayInfo?.sideLabelOne === 'string' ? bet.displayInfo.sideLabelOne : 'Side 1').trim();
  }
  if (sideKey === 'two') {
    return (typeof bet.displayInfo?.sideLabelTwo === 'string' ? bet.displayInfo.sideLabelTwo : 'Side 2').trim();
  }

  return sideKey || 'Unknown';
};

const resolveDetailedBetDescription = (
  bet: LeagueGameBetDefinition,
  propDefinitionById: Map<string, PropDefinition>,
): string | null => {
  if (bet.betType === 'moneyline') return 'Game Winner (Moneyline)';
  if (bet.betType === 'spread') return 'Point Spread';
  if (bet.betType === 'total') return 'Total Points (Over/Under)';
  if (bet.betType === 'total_tiebreaker') return 'Total Tiebreaker';

  if (bet.betType === 'custom_prop') {
    // Default to description in bets creator.
    const description = typeof bet.displayInfo?.description === 'string' ? bet.displayInfo.description.trim() : undefined;
    if (description) {
      return description;
    }

    // Fallback to describing bet manually from type.
    if (!bet.propDefinitionId) return null;
    const def = propDefinitionById.get(bet.propDefinitionId);
    if (!def) return null;

    let statLabel = 'Stat';
    switch (def.propType) {
      case 'PLAYER_VS_PLAYER_YARDS':
      case 'PLAYER_OVER_UNDER_YARDS':
        statLabel = 'Yards';
        break;
      case 'PLAYER_EITHER_REACHES_THRESHOLD': {
        const players = def.players.map((player) => player.fullName).join(' or ');
        const stat = def.statType?.replace(/_/g, ' ') ?? 'the stat line';
        const threshold = def.threshold === null || def.threshold === undefined ? 'the threshold' : def.threshold;
        return `Either ${players} to reach ${threshold} ${stat}`;
      }
      case 'PLAYER_VS_PLAYER_RECEPTIONS':
      case 'PLAYER_OVER_UNDER_RECEPTIONS':
        statLabel = 'Receptions';
        break;
      case 'PLAYER_VS_PLAYER_CARRIES':
      case 'PLAYER_OVER_UNDER_CARRIES':
        statLabel = 'Carries';
        break;
      case 'PLAYER_OVER_UNDER_INTERCEPTIONS':
        statLabel = 'Interceptions';
        break;
      case 'PLAYER_OVER_UNDER_SACKS':
        statLabel = 'Sacks';
        break;
      case 'ANYTIME_TD':
      case 'ANYTIME_TD_MULTI':
        statLabel = 'Anytime Touchdown';
        break;
      case 'RUSHING_TD':
        statLabel = 'Rushing Touchdown';
        break;
      case 'RECEIVING_TD':
        statLabel = 'Receiving Touchdown';
        break;
      case 'PASSING_TD':
        statLabel = 'Passing Touchdown';
        break;
      case 'LONGEST_STAT':
        statLabel = 'Longest Play';
        break;
      default:
        statLabel = def.propType.replace(/_/g, ' ').toLowerCase();
    }

    const players = def.players
      .map((p) => p.fullName)
      .join(' vs ');
    return `${players} ${statLabel}`;
  }
  return null;
};

interface WeeklyScoresProps {
  leagueId: string;
  scoringSystem?: 'oddsBased' | 'totalWins';
  seasonId?: string | null;
  scrollToUserToken?: number;
  verticalScrollOffsetRef?: RefObject<number>;
  verticalScrollRef?: RefObject<ScrollView | null>;
  verticalViewportRef?: RefObject<View | null>;
}

type WindowRect = { height: number; width: number; x: number; y: number };

function measureInWindow(
  node: { measureInWindow?: (callback: (x: number, y: number, width: number, height: number) => void) => void } | null,
  onMeasure: (rect: WindowRect) => void,
): boolean {
  if (!node || typeof node.measureInWindow !== 'function') {
    return false;
  }

  node.measureInWindow((x, y, width, height) => {
    onMeasure({ height, width, x, y });
  });
  return true;
}

const INACTIVE_PLAYER_DAYS = 30;
const INACTIVE_PLAYER_WINDOW_MS = INACTIVE_PLAYER_DAYS * 24 * 60 * 60 * 1000;

const isInactivePlayer = (memberUser: any, now = Date.now()): boolean => {
  if (!memberUser || !Object.prototype.hasOwnProperty.call(memberUser, 'lastActiveAt')) {
    return false;
  }

  if (memberUser.lastActiveAt == null) {
    return true;
  }

  const lastActiveAt = new Date(memberUser.lastActiveAt).getTime();
  return Number.isFinite(lastActiveAt) && now - lastActiveAt >= INACTIVE_PLAYER_WINDOW_MS;
};

type MemberEntry = {
  memberUser: any;
  weeklyPoints: number;
  seasonPoints: number;
  gamesCorrect: { correct: number; total: number };
  projectedGamesCorrect: { correct: number; total: number };
  projectedScore: number;
  baseScore: number;
  weeklyTiebreakerDelta: number;
  seasonTiebreakerDelta: number;
};

type RankedMemberEntry = MemberEntry & { rank: number };

const isFinalizedScoringBet = (
  bet: LeagueGameBetDefinition,
  game: Game,
  scoreContext: {
    homeTeam: Team;
    awayTeam: Team;
    homeScore: number | null;
    awayScore: number | null;
    winnerId: string | null;
  },
): boolean => {
  if (bet.betType === 'total_tiebreaker') return false;
  const resolvedSide = resolveWinningSide(bet, scoreContext) as BetSide | 'push' | null;
  if (resolvedSide === 'push') return false;
  if (bet.betType === 'custom_prop') return resolvedSide !== null;
  return game.isCompleted === true;
};

const getWeeklyScoresRankingValue = (
  entry: MemberEntry,
  scoringSystem: 'oddsBased' | 'totalWins',
  useLivePoints: boolean,
): number => {
  if (scoringSystem === 'totalWins') {
    return useLivePoints ? entry.projectedGamesCorrect.correct : entry.gamesCorrect.correct;
  }
  const points = useLivePoints ? entry.projectedScore : entry.baseScore;
  return Number(points.toFixed(1));
};

const assignRef = <T,>(ref: RefObject<T> | undefined, value: T) => {
  if (ref) {
    ref.current = value;
  }
};

const getMemberNameLines = (memberUser: {
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
} | null | undefined): string[] => {
  const firstName = memberUser?.firstName?.trim() ?? '';
  const lastName = memberUser?.lastName?.trim() ?? '';
  if (firstName && lastName) {
    return [firstName, lastName];
  }
  const emailName = memberUser?.email?.split('@')[0]?.trim();
  return [emailName || 'Unknown'];
};

const COMPACT_PLAYER_COLUMN_MIN = 44;
const COMPACT_PLAYER_COLUMN_MAX = 88;
const COMPACT_NAME_CELL_PADDING = 4;
const COMPACT_WEEK_CHROME = 24;
const COMPACT_RANK_COLUMN = 28;

// Space Grotesk 13px advances. Wide and narrow buckets keep the column near the glyphs.
const compactNameAdvance = (char: string): number => {
  if ('MWmw'.includes(char)) return 11.6;
  if ('Iijl'.includes(char)) return 3.6;
  if ('frt'.includes(char)) return 6.2;
  if (char === ' ') return 3.6;
  return 8.8;
};

const compactNameLineWidth = (line: string): number => {
  let width = 0;
  for (const char of line) width += compactNameAdvance(char);
  return Math.ceil(width);
};

const compactWeekTextWidth = (label: string, fontSize: number): number => {
  if (/^Week \d$/.test(label)) return (67.3 * fontSize) / 18;
  if (/^Week \d\d$/.test(label)) return (78.9 * fontSize) / 18;
  return label.length * fontSize * 0.56;
};

const resolveCompactPlayerColumn = (
  nameLines: readonly string[],
  weekLabel: string,
): { playerColumnWidth: number; weekFontSize: number } => {
  const longestName = nameLines.reduce((max, line) => Math.max(max, compactNameLineWidth(line)), 0);
  const nameColumn = Math.min(
    COMPACT_PLAYER_COLUMN_MAX,
    Math.max(COMPACT_PLAYER_COLUMN_MIN, longestName + COMPACT_NAME_CELL_PADDING),
  );
  for (const weekFontSize of [18, 17, 16, 15]) {
    const weekColumn = Math.ceil(compactWeekTextWidth(weekLabel, weekFontSize) + COMPACT_WEEK_CHROME - COMPACT_RANK_COLUMN);
    if (weekColumn <= nameColumn) {
      return { playerColumnWidth: nameColumn, weekFontSize };
    }
  }
  const weekColumn = Math.ceil(compactWeekTextWidth(weekLabel, 15) + COMPACT_WEEK_CHROME - COMPACT_RANK_COLUMN);
  return {
    playerColumnWidth: Math.min(COMPACT_PLAYER_COLUMN_MAX, Math.max(nameColumn, weekColumn)),
    weekFontSize: 15,
  };
};

export default function WeeklyScores({
  leagueId,
  scoringSystem = 'oddsBased',
  seasonId,
  scrollToUserToken = 0,
  verticalScrollOffsetRef,
  verticalScrollRef,
  verticalViewportRef,
}: WeeklyScoresProps) {
  const { user } = useAuth();
  const insets = useSafeAreaInsets();
  const isCurrentUserMember = useCallback((memberUser: any): boolean => {
    if (!memberUser || !user) {
      return false;
    }

    const memberId = memberUser.id;
    const currentUserId = user.id;
    if (memberId != null && currentUserId != null && String(memberId) === String(currentUserId)) {
      return true;
    }

    return Boolean(
      memberUser.email &&
        user.email &&
        String(memberUser.email).trim().toLowerCase() === String(user.email).trim().toLowerCase(),
    );
  }, [user]);
  const trpc = useTRPC();
  const [selectedWeekId, setSelectedWeekId] = useState<string | null>(null);
  const [includeLivePoints, setIncludeLivePoints] = useState(true);
  const [pointsColumnShowsCorrect, setPointsColumnShowsCorrect] = useState(false);
  const [viewMode, setViewMode] = useState<'weekly' | 'season'>('weekly');
  const { width: screenWidth } = useWindowDimensions();
  const isCompactScreen = screenWidth < 480;
  const isWideScreen = screenWidth >= 1024;
  const rankColumnWidth = isCompactScreen ? COMPACT_RANK_COLUMN : isWideScreen ? 44 : 36;
  const basePlayerColumnWidth = isWideScreen ? 168 : 132;
  const pointsColumnWidth = isCompactScreen ? 66 : isWideScreen ? 74 : 66;
  const gameColumnWidth = isCompactScreen ? 80 : isWideScreen ? 120 : 108;
  const scoreHeaderHeight = isCompactScreen ? 108 : isWideScreen ? 120 : 112;
  const winningPointsRowHeight = 40;
  const playerRowHeight = isCompactScreen ? 60 : isWideScreen ? 50 : 54;
  const showWinningPointsRow = false;
  const headerScrollRef = useRef<ScrollView>(null);
  const bodyScrollRef = useRef<ScrollView>(null);
  const topScrollbarRef = useRef<ScrollView>(null);
  const bottomScrollbarRef = useRef<ScrollView>(null);
  const horizontalScrollOffsetRef = useRef(0);
  const pendingHorizontalSyncRef = useRef<{ offset: number; source: HorizontalScrollPane } | null>(null);
  const horizontalSyncFrameRef = useRef<number | null>(null);
  const isProgrammaticHorizontalSyncRef = useRef(false);
  const currentUserRowRef = useRef<View>(null);
  const completedScrollKeyRef = useRef<string | null>(null);
  const pendingScrollKeyRef = useRef<string | null>(null);
  const scrollRequestKeyRef = useRef<string | null>(null);

  // Keep header, body, and the always-visible web scrollbars in lockstep.
  // Ignore onScroll echoes from programmatic scrollTo so the panes cannot drift.
  const syncHorizontalScroll = useCallback((source: HorizontalScrollPane, event: any) => {
    if (isProgrammaticHorizontalSyncRef.current) {
      return;
    }

    const offset = event?.nativeEvent?.contentOffset?.x ?? event?.nativeEvent?.target?.scrollLeft ?? 0;
    if (Math.abs(offset - horizontalScrollOffsetRef.current) < 0.5) {
      return;
    }

    horizontalScrollOffsetRef.current = offset;
    pendingHorizontalSyncRef.current = { offset, source };
    if (horizontalSyncFrameRef.current !== null) {
      return;
    }

    const flushHorizontalSync = () => {
      horizontalSyncFrameRef.current = null;
      const pendingSync = pendingHorizontalSyncRef.current;
      pendingHorizontalSyncRef.current = null;
      if (!pendingSync) {
        return;
      }

      const targets = [
        pendingSync.source === 'header' ? null : headerScrollRef.current,
        pendingSync.source === 'body' ? null : bodyScrollRef.current,
        pendingSync.source === 'top' ? null : topScrollbarRef.current,
        pendingSync.source === 'bottom' ? null : bottomScrollbarRef.current,
      ].filter((target): target is ScrollView => target != null);

      if (targets.length === 0) {
        return;
      }

      isProgrammaticHorizontalSyncRef.current = true;
      targets.forEach((target) => {
        target.scrollTo({ x: pendingSync.offset, animated: false });
      });

      const unlock = () => {
        isProgrammaticHorizontalSyncRef.current = false;
      };
      if (typeof globalThis.requestAnimationFrame === 'function') {
        globalThis.requestAnimationFrame(() => {
          globalThis.requestAnimationFrame(unlock);
        });
      } else {
        unlock();
      }
    };

    if (typeof globalThis.requestAnimationFrame === 'function') {
      horizontalSyncFrameRef.current = globalThis.requestAnimationFrame(flushHorizontalSync);
    } else {
      flushHorizontalSync();
    }
  }, []);

  const { data: currentActiveWeek } = useQuery(
    trpc.nfl.currentWeek.queryOptions(),
  );

  const {
    data: seasonWeeks,
    isLoading: seasonWeeksLoading,
  } = useQuery<LeagueSeasonWeeksPayload | null>({
    queryKey: ['/api/leagues', leagueId, 'season-weeks', seasonId ?? 'none'],
    enabled: !!leagueId && !!seasonId,
    queryFn: async () => {
      if (!leagueId || !seasonId) {
        return null;
      }
      const params = new URLSearchParams();
      params.set('seasonId', seasonId);
      try {
        return await apiRequestJson<LeagueSeasonWeeksPayload>(
          `/api/leagues/${leagueId}/weeks?${params.toString()}`,
          'GET',
        );
      } catch (error) {
        if (__DEV__) {
          clientLogger.warn('Falling back to legacy week list for Weekly Scores', { error });
        }
        const fallbackWeeks = await trpcClient.nfl.weeks.query();
        return {
          resolvedSeasonId: seasonId,
          weeks: fallbackWeeks.map((week: any, index: number) => ({
            id: week.id,
            leagueWeekId: week.id,
            label: week.label || week.id,
            displayLabel:
              typeof week.week === 'number' ? `Week ${week.week}` : week.label || `Week ${index + 1}`,
            weekNumber: typeof week.week === 'number' ? week.week : index + 1,
            startsAt: week.startDate ?? week.startsAt ?? null,
            endsAt: week.endDate ?? week.endsAt ?? null,
          })),
        } satisfies LeagueSeasonWeeksPayload;
      }
    },
  });

  const seasonWeekOptions = useMemo(
    () => filterPastAndActiveWeeks(seasonWeeks?.weeks ?? [], currentActiveWeek, getCurrentTime()),
    [currentActiveWeek, seasonWeeks],
  );

  useEffect(() => {
    if (!seasonWeekOptions.length) return;
    if (selectedWeekId && seasonWeekOptions.some((week) => week.id === selectedWeekId)) {
      return;
    }
    const currentWeekNumber = currentActiveWeek?.week;
    const fallbackWeek =
      (typeof currentWeekNumber === 'number'
        ? seasonWeekOptions.find((week) => week.weekNumber === currentWeekNumber)
        : null) ?? seasonWeekOptions[0];
    if (fallbackWeek) {
      setSelectedWeekId(fallbackWeek.id);
    }
  }, [seasonWeekOptions, selectedWeekId, currentActiveWeek?.week]);

  const selectedWeekOption = useMemo(() => {
    if (!seasonWeekOptions.length) return null;
    if (selectedWeekId) {
      const match = seasonWeekOptions.find((week) => week.id === selectedWeekId);
      if (match) {
        return match;
      }
    }
    return seasonWeekOptions[0];
  }, [seasonWeekOptions, selectedWeekId]);

  const currentWeekNumber = useMemo(() => {
    if (typeof selectedWeekOption?.weekNumber === 'number') {
      return selectedWeekOption.weekNumber;
    }
    if (typeof seasonWeekOptions[0]?.weekNumber === 'number') {
      return seasonWeekOptions[0].weekNumber;
    }
    return currentActiveWeek?.week ?? null;
  }, [selectedWeekOption, seasonWeekOptions, currentActiveWeek?.week]);

  const { data: leagueMembers, isLoading: membersLoading } = useQuery(
    trpc.leagues.members.queryOptions(
      {
        leagueId,
        seasonId: seasonId ?? undefined,
      },
      {
        enabled: !!seasonId,
      },
    ),
  );
  const {
    data: leagueWeekGames,
    isLoading: leagueWeekGamesLoading,
  } = useQuery<LeagueWeekGamesPayload | null>({
    queryKey: [
      '/api/leagues',
      leagueId,
      'weeks',
      selectedWeekOption?.id ?? 'none',
      seasonId ?? 'none',
      'scores',
    ],
    enabled: !!selectedWeekOption?.id && !!seasonId,
    refetchInterval: 60_000,
    refetchIntervalInBackground: true,
    queryFn: async () => {
      if (!selectedWeekOption?.id || !seasonId) {
        return null;
      }
      const params = new URLSearchParams();
      params.set('seasonId', seasonId);
      try {
        return await apiRequestJson<LeagueWeekGamesPayload>(
          `/api/leagues/${leagueId}/weeks/${selectedWeekOption.id}/games?${params.toString()}`,
          'GET',
        );
      } catch (error) {
        if (__DEV__) {
          clientLogger.warn(
            'Falling back to legacy weekly scores game data for week',
            {
              weekId: selectedWeekOption.id,
              seasonId,
              error,
            },
          );
        }
        const fallback = await trpcClient.nfl.gamesByWeek.query({ weekId: selectedWeekOption.id });
        return {
          leagueWeekId: null,
          resolvedSeasonId: seasonId,
          legacy: true,
          games: fallback.map((game) => ({
            game,
            bets: [
              {
                id: `${game.id}-moneyline`,
                leagueWeekBetId: null,
                betType: 'moneyline' as const,
                displayInfo: {},
                oddsOne: typeof game.homeOdds === 'number' ? game.homeOdds : game.homeOdds ? Number(game.homeOdds) : null,
                oddsTwo: typeof game.awayOdds === 'number' ? game.awayOdds : game.awayOdds ? Number(game.awayOdds) : null,
              },
            ],
          })),
        };
      }
    },
  });
  const gameGroups = useMemo<LeagueWeekGamesPayload['games']>(() => leagueWeekGames?.games ?? [], [leagueWeekGames]);
  const sortedGameGroups = useMemo(() => {
    if (!gameGroups.length) return [];
    return [...gameGroups].sort((a, b) => {
      const timeDiff =
        new Date(a.game.gameTime).getTime() - new Date(b.game.gameTime).getTime();
      if (timeDiff !== 0) return timeDiff;

      const homeA =
        a.game.homeTeam?.name ||
        a.game.homeTeam?.abbreviation ||
        a.game.homeTeam?.city ||
        '';
      const homeB =
        b.game.homeTeam?.name ||
        b.game.homeTeam?.abbreviation ||
        b.game.homeTeam?.city ||
        '';
      return homeA.localeCompare(homeB);
    });
  }, [gameGroups]);
  const sortedGames = useMemo(() => sortedGameGroups.map((group) => group.game), [sortedGameGroups]);
  const gamesLoading = leagueWeekGamesLoading;

  const { data: leagueData } = useQuery(
    trpc.leagues.detail.queryOptions(
      {
        leagueId,
        seasonId: seasonId ?? undefined,
      },
      {
        enabled: !!leagueId && !!seasonId,
      },
    ),
  );

  const scoringConfig =
    (leagueData?.currentSeason?.scoringConfig as Record<string, unknown> | null) ?? null;
  const resolvedScoringSystem = resolveLegacyScoringSystem(scoringSystem, scoringConfig);
  const showPointsColumn = resolvedScoringSystem !== 'totalWins';
  const correctColumnWidth = isCompactScreen ? 58 : isWideScreen ? 72 : 64;
  const seasonWeekMode = (scoringConfig as { seasonWeekMode?: string } | null)?.seasonWeekMode || 'standard';
  const shouldUseCumulativeSeasonPoints = seasonWeekMode === 'propWeeklyOnly' && viewMode === 'season';
  const effectiveWeekNumber = typeof currentWeekNumber === 'number' ? currentWeekNumber : null;

  const { data: leaderboardEntries, isLoading: leaderboardLoading } = useQuery(
    trpc.leagues.leaderboard.queryOptions(
      {
        leagueId,
        seasonId: seasonId ?? undefined,
        weekNumber: typeof effectiveWeekNumber === 'number' ? effectiveWeekNumber : undefined,
      },
      {
        enabled:
          !!seasonId &&
          !!leagueId &&
          seasonWeekMode === 'propWeeklyOnly' &&
          typeof effectiveWeekNumber === 'number',
      },
    ),
  );

  const hasExclusions = useMemo(() => {
    if (seasonWeekMode === 'propWeeklyOnly') return true;
    if (!sortedGameGroups.length) return false;
    return sortedGameGroups.some(group =>
      group.bets.some(bet =>
        bet.scoringConfigOverride?.inclusion?.weekly === false ||
        bet.scoringConfigOverride?.inclusion?.season === false
      )
    );
  }, [sortedGameGroups, seasonWeekMode]);

  const propDefinitionIds = useMemo(() => {
    return sortedGameGroups
      .flatMap((group) => group.bets)
      .filter((bet) => bet.betType === 'custom_prop' && bet.propDefinitionId)
      .map((bet) => bet.propDefinitionId as string);
  }, [sortedGameGroups]);

  const { data: propDefinitions } = useQuery(
    trpc.leagues.propDefinitions.queryOptions(
      {
        leagueId,
        weekId: selectedWeekOption?.id ?? '',
        seasonId: seasonId ?? undefined,
      },
      {
        enabled: !!leagueId && !!seasonId && !!selectedWeekOption?.id && propDefinitionIds.length > 0,
      },
    ),
  );

  const { data: propEvaluations } = useQuery(
    trpc.leagues.propEvaluations.queryOptions(
      {
        leagueId,
        weekId: selectedWeekOption?.id ?? '',
        seasonId: seasonId ?? undefined,
      },
      {
        enabled: !!leagueId && !!seasonId && !!selectedWeekOption?.id && propDefinitionIds.length > 0,
      },
    ),
  );

  const propDefinitionById = useMemo(() => {
    const map = new Map<string, PropDefinition>();
    (propDefinitions ?? []).forEach((definition) => {
      map.set(definition.id, definition);
    });
    return map;
  }, [propDefinitions]);

  const propEvaluationByDefinitionId = useMemo(() => {
    const map = new Map<string, PropEvaluation>();
    (propEvaluations ?? []).forEach((evaluation) => {
      map.set(evaluation.propDefinitionId, evaluation);
    });
    return map;
  }, [propEvaluations]);

  const allBetColumns = useMemo<{ game: Game; bet: LeagueGameBetDefinition; betKey: string }[]>(() => {
    return sortedGameGroups.flatMap((group) =>
      (group.bets ?? []).map((bet: LeagueGameBetDefinition) => ({
        game: group.game,
        bet,
        betKey: buildBetKey(group.game.id, bet),
      })),
    );
  }, [sortedGameGroups]);

  const betColumnsByKey = useMemo(() => {
    const map = new Map<string, { game: Game; bet: LeagueGameBetDefinition }>();
    allBetColumns.forEach(({ game, bet, betKey }) => {
      map.set(betKey, { game, bet });
    });
    return map;
  }, [allBetColumns]);

  const weeklyTiebreakerColumn = useMemo(() => {
    const tiebreakers = allBetColumns.filter(({ bet }) => bet.betType === 'total_tiebreaker');
    if (!tiebreakers.length) return null;
    return tiebreakers.reduce((latest, current) => {
      const latestTime = new Date(latest.game.gameTime).getTime();
      const currentTime = new Date(current.game.gameTime).getTime();
      return currentTime > latestTime ? current : latest;
    });
  }, [allBetColumns]);

  const betColumns = useMemo(() => {
    if (!hasExclusions) return allBetColumns;
    return allBetColumns.filter(({ bet }) => {
      // propWeeklyOnly mode: Weekly = props + totals, Season = moneyline + spread
      if (seasonWeekMode === 'propWeeklyOnly') {
        const isWeekly = bet.betType === 'custom_prop' || bet.betType === 'total';
        return viewMode === 'weekly' ? isWeekly : !isWeekly;
      }

      // Fallback for legacy inclusion flags if they exist
      const includeWeekly = bet.scoringConfigOverride?.inclusion?.weekly ?? true;
      const includeSeason = bet.scoringConfigOverride?.inclusion?.season ?? true;

      return viewMode === 'weekly' ? includeWeekly : includeSeason;
    });
  }, [allBetColumns, hasExclusions, viewMode, seasonWeekMode]);

  const [measuredScrollableWidth, setMeasuredScrollableWidth] = useState(0);
  const computedScrollableContentWidth = useMemo(() => {
    return betColumns.length * gameColumnWidth + 4;
  }, [betColumns.length, gameColumnWidth]);
  const scrollableContentWidth = measuredScrollableWidth > 0
    ? measuredScrollableWidth
    : computedScrollableContentWidth;

  const { data: picks, isLoading: picksLoading } = useQuery(
    trpc.leagues.picksByWeek.queryOptions(
      {
        leagueId,
        week: effectiveWeekNumber ?? 0,
        seasonId: seasonId ?? undefined,
      },
      {
        enabled: !!seasonId && !!selectedWeekOption?.id && typeof effectiveWeekNumber === 'number',
      },
    ),
  );

  const isLoading =
    !seasonId ||
    membersLoading ||
    gamesLoading ||
    picksLoading ||
    seasonWeeksLoading ||
    (shouldUseCumulativeSeasonPoints && leaderboardLoading);

  // Create a map of user picks by game
  const picksByUserAndBet = useMemo(() => {
    const map = new Map<string, Map<string, Pick>>();
    picks?.forEach(pick => {
      if (!map.has(pick.userId)) {
        map.set(pick.userId, new Map());
      }
      const betKey = derivePickKey(pick);
      map.get(pick.userId)?.set(betKey, pick);
    });
    return map;
  }, [picks]);

  const getWeeklyTiebreakerDelta = useCallback((userId: string): number => {
    if (!weeklyTiebreakerColumn) return Number.POSITIVE_INFINITY;
    // Final standings wait until the tiebreaker game is over. A live total can
    // only reorder the Live view, and only inside an existing score tie.
    if (!includeLivePoints && !weeklyTiebreakerColumn.game.isCompleted) {
      return Number.POSITIVE_INFINITY;
    }
    const pick = picksByUserAndBet.get(userId)?.get(weeklyTiebreakerColumn.betKey);
    const guess = pick?.details?.tiebreakerGuess;
    if (typeof guess !== 'number') return Number.POSITIVE_INFINITY;
    const homeScore =
      typeof weeklyTiebreakerColumn.game.homeScore === 'number' ? weeklyTiebreakerColumn.game.homeScore : null;
    const awayScore =
      typeof weeklyTiebreakerColumn.game.awayScore === 'number' ? weeklyTiebreakerColumn.game.awayScore : null;
    if (homeScore === null || awayScore === null) return Number.POSITIVE_INFINITY;
    return Math.abs(homeScore + awayScore - guess);
  }, [includeLivePoints, picksByUserAndBet, weeklyTiebreakerColumn]);

  const buildGameScoreContext = useCallback((game: Game) => ({
    homeTeam: game.homeTeam,
    awayTeam: game.awayTeam,
    homeScore: typeof game.homeScore === 'number' ? game.homeScore : null,
    awayScore: typeof game.awayScore === 'number' ? game.awayScore : null,
    winnerId: game.winnerId ?? null,
  }), []);

  const buildGameOddsContext = useCallback((game: Game) => ({
    homeOdds: game.homeOdds ?? null,
    awayOdds: game.awayOdds ?? null,
  }), []);

  const getPickStatus = useCallback((userId: string, column: { game: Game; bet: LeagueGameBetDefinition; betKey: string }) => {
    const userPicks = picksByUserAndBet.get(userId);
    const pick = userPicks?.get(column.betKey);

    if (!pick) return null;
    const resolvedSide = resolveWinningSide(
      column.bet,
      buildGameScoreContext(column.game),
    ) as BetSide | 'push' | null;
    const isResolved =
      column.bet.betType === 'custom_prop' ? resolvedSide !== null : column.game.isCompleted;
    if (isResolved && resolvedSide === 'push') return 'push';
    if (pick.isCorrect !== null) return pick.isCorrect ? 'correct' : 'incorrect';

    if (column.bet.betType === 'custom_prop') {
      const propEvaluation = column.bet.propDefinitionId
        ? propEvaluationByDefinitionId.get(column.bet.propDefinitionId)
        : null;
      if (propEvaluation?.resolvedWinningSide) {
        const pickedSide = getSideFromPick(pick, column.game);
        if (pickedSide === propEvaluation.resolvedWinningSide) return 'correct';
        return 'incorrect';
      }
      if (resolvedSide && resolvedSide !== 'push') {
        const pickedSide = getSideFromPick(pick, column.game);
        if (pickedSide === resolvedSide) return 'correct';
        return 'incorrect';
      }
    }

    return 'pending';
  }, [picksByUserAndBet, buildGameScoreContext, propEvaluationByDefinitionId]);

  const getWinningPoints = (game: Game, bet: LeagueGameBetDefinition): string => {
    if (bet.betType === 'total_tiebreaker') {
      return '—';
    }
    const resolvedSide = resolveWinningSide(
      bet,
      buildGameScoreContext(game),
    ) as BetSide | 'push' | null;
    if (resolvedSide === 'push') {
      return 'PUSH';
    }
    const winningSide =
      resolvedSide ??
      (() => {
        if (bet.betType === 'custom_prop' && bet.propDefinitionId) {
          const evaluation = propEvaluationByDefinitionId.get(bet.propDefinitionId);
          if (evaluation?.leadingSide === 'one' || evaluation?.leadingSide === 'two') {
            return evaluation.leadingSide as BetSide;
          }
          return null;
        }
        const homeState = getBetLiveState(bet, buildGameScoreContext(game), 'one');
        if (homeState === 'leading') return 'one';
        const awayState = getBetLiveState(bet, buildGameScoreContext(game), 'two');
        if (awayState === 'leading') return 'two';
        return null;
      })();

    if (!winningSide) return '—';

    const pointsValue = getBetPoints({
      scoringSystem,
      scoringConfig,
      bet,
      side: winningSide as BetSide,
      gameOdds: buildGameOddsContext(game),
    });
    const formatted =
      resolvedScoringSystem === 'totalWins'
        ? `${Math.round(pointsValue)}`
        : pointsValue.toFixed(1);
    return `${formatted} pts`;
  };

  const getUserWeeklyPoints = useCallback((userId: string): number => {
    if (!userId || !picks) return 0;

    let totalPoints = 0;
    picks.forEach(pick => {
      if (pick.userId === userId) {
        if (pick.betType === 'total_tiebreaker') {
          return;
        }
        let include = true;
        if (seasonWeekMode === 'propWeeklyOnly') {
          include = pick.betType === 'custom_prop' || pick.betType === 'total';
        } else {
          include = pick.scoringConfigOverride?.inclusion?.weekly ?? true;
        }

        if (include) {
          const betEntry = betColumnsByKey.get(derivePickKey(pick));
          if (!betEntry) {
            totalPoints += Number(pick.pointsEarned) || 0;
            return;
          }

          const side = getSideFromPick(pick, betEntry.game) as BetSide | null;
          if (!side) return;

          const scoreContext = buildGameScoreContext(betEntry.game);
          if (!isFinalizedScoringBet(betEntry.bet, betEntry.game, scoreContext)) return;

          const isCorrect = isPickCorrect(
            { selectedTeamId: pick.selectedTeamId, side },
            scoreContext,
            betEntry.bet,
          );
          if (!isCorrect) return;

          totalPoints += getBetPoints({
            scoringSystem,
            scoringConfig,
            bet: betEntry.bet,
            side,
            gameOdds: buildGameOddsContext(betEntry.game),
          });
        }
      }
    });

    return totalPoints;
  }, [
    picks,
    seasonWeekMode,
    scoringSystem,
    scoringConfig,
    betColumnsByKey,
    buildGameScoreContext,
    buildGameOddsContext,
  ]);

  const getUserSeasonPoints = useCallback((userId: string): number => {
    if (!userId || !picks) return 0;

    let totalPoints = 0;
    picks.forEach(pick => {
      if (pick.userId === userId) {
        if (pick.betType === 'total_tiebreaker') {
          return;
        }
        let include = true;
        if (seasonWeekMode === 'propWeeklyOnly') {
          const isWeekly = pick.betType === 'custom_prop' || pick.betType === 'total';
          include = !isWeekly;
        } else {
          include = pick.scoringConfigOverride?.inclusion?.season ?? true;
        }

        if (include) {
          const betEntry = betColumnsByKey.get(derivePickKey(pick));
          if (!betEntry) {
            totalPoints += Number(pick.pointsEarned) || 0;
            return;
          }

          const side = getSideFromPick(pick, betEntry.game) as BetSide | null;
          if (!side) return;

          const scoreContext = buildGameScoreContext(betEntry.game);
          if (!isFinalizedScoringBet(betEntry.bet, betEntry.game, scoreContext)) return;

          const isCorrect = isPickCorrect(
            { selectedTeamId: pick.selectedTeamId, side },
            scoreContext,
            betEntry.bet,
          );
          if (!isCorrect) return;

          totalPoints += getBetPoints({
            scoringSystem,
            scoringConfig,
            bet: betEntry.bet,
            side,
            gameOdds: buildGameOddsContext(betEntry.game),
          });
        }
      }
    });

    return totalPoints;
  }, [
    picks,
    seasonWeekMode,
    scoringSystem,
    scoringConfig,
    betColumnsByKey,
    buildGameScoreContext,
    buildGameOddsContext,
  ]);

  const getUserGamesCorrect = useCallback((userId: string): { correct: number; total: number } => {
    if (!userId || !picks || betColumns.length === 0) return { correct: 0, total: 0 };

    const completedColumns = betColumns.filter(({ game, bet }) => {
      if (bet.betType === 'total_tiebreaker') return false;
      const winningSide = resolveWinningSide(
        bet,
        buildGameScoreContext(game),
      ) as BetSide | 'push' | null;
      const isResolved = bet.betType === 'custom_prop' ? winningSide !== null : game.isCompleted;
      if (!isResolved) return false;
      return winningSide !== 'push';
    });
    const totalCompleted = completedColumns.length;

    const correctCount = completedColumns.reduce((sum, column) => {
      const pick = picks.find((p) => p.userId === userId && derivePickKey(p) === column.betKey);
      if (pick?.betType === 'total_tiebreaker') return sum;
      if (pick && pick.isCorrect) {
        return sum + 1;
      }
      return sum;
    }, 0);

    return { correct: correctCount, total: totalCompleted };
  }, [betColumns, buildGameScoreContext, picks]);

  const hasGameStarted = useCallback((gameTime: string): boolean => {
    const currentTime = getCurrentTime();
    return new Date(gameTime).getTime() <= currentTime.getTime();
  }, []);

  const isGameInProgress = useCallback((game: Game): boolean => {
    if (!game) return false;
    return hasGameStarted(game.gameTime) && !game.isCompleted;
  }, [hasGameStarted]);

  const doesUserLeadGame = useCallback((
    bet: LeagueGameBetDefinition,
    game: Game,
    pick?: Pick | null,
  ): 'leading' | 'trailing' | 'tied' => {
    if (!pick) return 'tied';
    const side = getSideFromPick(pick, game);
    if (!side) return 'tied';

    if (bet.betType === 'custom_prop' && bet.propDefinitionId) {
      const evaluation = propEvaluationByDefinitionId.get(bet.propDefinitionId);
      if (!evaluation?.leadingSide) {
        return 'tied';
      }
      return evaluation.leadingSide === side ? 'leading' : 'trailing';
    }

    return getBetLiveState(bet, buildGameScoreContext(game), side as BetSide);
  }, [buildGameScoreContext, propEvaluationByDefinitionId]);

  const getUserProjectedGamesCorrect = useCallback((userId: string): { correct: number; total: number } => {
    if (!userId || !picks || betColumns.length === 0) return { correct: 0, total: 0 };

    const base = getUserGamesCorrect(userId);
    const inProgressColumns = betColumns.filter(({ game, bet }) => {
      if (bet.betType === 'total_tiebreaker') return false;
      if (!isGameInProgress(game)) return false;
      if (bet.betType !== 'custom_prop') return true;
      const winningSide = resolveWinningSide(
        bet,
        buildGameScoreContext(game),
      ) as BetSide | 'push' | null;
      return winningSide === null;
    });
    if (inProgressColumns.length === 0) {
      return base;
    }

    const projectedCorrect = inProgressColumns.reduce((sum, column) => {
      const pick = picksByUserAndBet.get(userId)?.get(column.betKey);
      if (!pick || pick.betType === 'total_tiebreaker') return sum;
      const side = getSideFromPick(pick, column.game) as BetSide | null;
      if (!side) return sum;
      const liveState = doesUserLeadGame(column.bet, column.game, pick);
      return liveState === 'leading' ? sum + 1 : sum;
    }, base.correct);

    return { correct: projectedCorrect, total: base.total + inProgressColumns.length };
  }, [betColumns, buildGameScoreContext, doesUserLeadGame, getUserGamesCorrect, isGameInProgress, picks, picksByUserAndBet]);

  const shouldShowPick = useCallback((pickUserId: string, game: Game): boolean => {
    const isCurrentUser = pickUserId === user?.id;
    const gameHasStarted = hasGameStarted(game.gameTime);
    return isCurrentUser || gameHasStarted;
  }, [hasGameStarted, user?.id]);

  const getMaximumWeeklyPoints = (): number => {
    if (!betColumns.length) return 0;

    let totalPoints = 0;
    betColumns.forEach(({ game, bet }) => {
      const winningSide = resolveWinningSide(
        bet,
        buildGameScoreContext(game),
      ) as BetSide | 'push' | null;
      if (!winningSide || winningSide === 'push') {
        return;
      }
      totalPoints += getBetPoints({
        scoringSystem,
        scoringConfig,
        bet,
        side: winningSide as BetSide,
        gameOdds: buildGameOddsContext(game),
      });
    });

    return totalPoints;
  };

  const projectedScoreForUser = useCallback((userId: string, baseScore: number): number => {
    if (!betColumns.length) return baseScore;
    let projectedScore = baseScore;

    betColumns.forEach(({ game, bet, betKey }) => {
      if (bet.betType === 'total_tiebreaker') return;
      if (!isGameInProgress(game)) return;
      const userPick = picksByUserAndBet.get(userId)?.get(betKey);
      if (!userPick) return;

      const side = getSideFromPick(userPick, game) as BetSide | null;
      if (!side) return;

      const liveStatus = doesUserLeadGame(bet, game, userPick);
      if (liveStatus !== 'leading') return;

      projectedScore += getBetPoints({
        scoringSystem,
        scoringConfig,
        bet,
        side,
        gameOdds: buildGameOddsContext(game),
      });
    });

    return projectedScore;
  }, [
    betColumns,
    buildGameOddsContext,
    doesUserLeadGame,
    isGameInProgress,
    picksByUserAndBet,
    scoringSystem,
    scoringConfig,
  ]);

  const formatGameScoreLine = (game: Game): string => {
    const awayScore = typeof game.awayScore === 'number' ? game.awayScore : null;
    const homeScore = typeof game.homeScore === 'number' ? game.homeScore : null;
    if (awayScore === null || homeScore === null) {
      return hasGameStarted(game.gameTime) ? 'Live scoring…' : 'No score yet';
    }
    return `${game.awayTeam.abbreviation} ${awayScore} - ${homeScore} ${game.homeTeam.abbreviation}`;
  };

  const formatBetOutcome = (game: Game, bet: LeagueGameBetDefinition): string => {
    if (bet.betType === 'total') {
      const homeScore = typeof game.homeScore === 'number' ? game.homeScore : null;
      const awayScore = typeof game.awayScore === 'number' ? game.awayScore : null;
      if (homeScore !== null && awayScore !== null) {
        return `Total: ${homeScore + awayScore}`;
      }
      return hasGameStarted(game.gameTime) ? 'Live scoring…' : 'No score yet';
    }
    if (bet.betType === 'total_tiebreaker') {
      const homeScore = typeof game.homeScore === 'number' ? game.homeScore : null;
      const awayScore = typeof game.awayScore === 'number' ? game.awayScore : null;
      if (homeScore !== null && awayScore !== null) {
        return `Total: ${homeScore + awayScore}`;
      }
      return hasGameStarted(game.gameTime) ? 'Live scoring…' : 'No score yet';
    }

    if (bet.betType === 'custom_prop') {
      const winningSide = (bet.displayInfo as any)?.winningSide;
      const outcomeLabel = (bet.displayInfo as any)?.outcomeLabel;

      if (outcomeLabel) {
        return outcomeLabel;
      }
      if (winningSide) {
        return resolveCustomPropSideLabel(bet, winningSide);
      }
      return 'Pending';
    }

    return formatGameScoreLine(game);
  };

  const formatPropStatLine = useCallback((bet: LeagueGameBetDefinition): string | null => {
    if (!bet.propDefinitionId) {
      return null;
    }
    const definition = propDefinitionById.get(bet.propDefinitionId);
    const evaluation = propEvaluationByDefinitionId.get(bet.propDefinitionId);
    if (!definition || !evaluation) {
      return null;
    }

    return formatSharedPropStatLine({
      propType: definition.propType,
      threshold: definition.threshold,
      displayConfig: definition.displayConfig,
      players: definition.players,
    }, toNumericRecord(evaluation.currentValues));
  }, [propDefinitionById, propEvaluationByDefinitionId]);

  const formatGameStatusLabel = (game: Game): string => {
    if (game.isCompleted) return 'FINAL';
    if (isGameInProgress(game)) {
      const normalizedStatus = game.status?.toLowerCase();
      if (normalizedStatus === 'halftime') return 'LIVE · HALFTIME';
      if (normalizedStatus === 'end_period') return 'LIVE · END QTR';
      return 'LIVE';
    }
    const kickoff = new Date(game.gameTime);
    if (Number.isNaN(kickoff.getTime())) return 'UPCOMING';
    const weekday = kickoff.toLocaleDateString(undefined, { weekday: 'short' });
    const time = kickoff.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    return `${weekday} · ${time}`.toUpperCase();
  };

  const formatBetLabel = (bet: LeagueGameBetDefinition): string => {
    if (bet.betType === 'moneyline') return 'Moneyline';
    if (bet.betType === 'spread') {
      return `${formatSpread(-(bet.displayInfo?.spread as number))}        ${formatSpread(bet.displayInfo?.spread as number)}`;
    }
    if (bet.betType === 'total') return `O/U ${formatTotal(bet.displayInfo?.total as number)}`;
    if (bet.betType === 'total_tiebreaker') return 'Tiebreaker';
    return resolveCustomPropLabel(bet);
  };

  const hasLiveGames = useMemo(() => {
    if (!sortedGames.length) return false;
    return sortedGames.some(game => isGameInProgress(game));
  }, [sortedGames, isGameInProgress]);

  const leaderboardByUserId = useMemo(() => {
    return new Map((leaderboardEntries ?? []).map((entry) => [entry.userId, entry]));
  }, [leaderboardEntries]);

  const memberEntries: MemberEntry[] = useMemo(() => {
    return (leagueMembers || [])
      .filter((member) => !isInactivePlayer(member.users || member.user))
      .map((member) => {
        const memberUser = member.users || member.user;
        const userId = memberUser?.id || '';
        const weeklyPoints = getUserWeeklyPoints(userId);
        const seasonPoints = shouldUseCumulativeSeasonPoints
          ? leaderboardByUserId.get(userId)?.totalPoints ?? 0
          : getUserSeasonPoints(userId);

        const gamesCorrect = getUserGamesCorrect(userId);
        const projectedGamesCorrect = getUserProjectedGamesCorrect(userId);

        const baseScore = viewMode === 'season' ? seasonPoints : weeklyPoints;
        const projectedScore = projectedScoreForUser(userId, baseScore);
        const weeklyTiebreakerDelta = getWeeklyTiebreakerDelta(userId);
        const seasonTiebreakerDelta = leaderboardByUserId.get(userId)?.tiebreakerDelta ?? Number.POSITIVE_INFINITY;

        return {
          memberUser,
          weeklyPoints,
          seasonPoints,
          gamesCorrect,
          projectedGamesCorrect,
          projectedScore,
          baseScore,
          weeklyTiebreakerDelta,
          seasonTiebreakerDelta,
        };
      });
  }, [
    leagueMembers,
    getUserWeeklyPoints,
    getUserSeasonPoints,
    getUserGamesCorrect,
    getUserProjectedGamesCorrect,
    leaderboardByUserId,
    getWeeklyTiebreakerDelta,
    projectedScoreForUser,
    shouldUseCumulativeSeasonPoints,
    viewMode,
  ]);

  const sortedMembers = useMemo((): RankedMemberEntry[] => {
    const entries = [...memberEntries];

    const getRankingInput = (entry: MemberEntry) => ({
      rankingValue: getWeeklyScoresRankingValue(entry, resolvedScoringSystem, includeLivePoints),
      tiebreakerDelta: viewMode === 'weekly' ? entry.weeklyTiebreakerDelta : entry.seasonTiebreakerDelta,
    });

    entries.sort((a, b) => {
      const compared = compareScoreThenTiebreaker(getRankingInput(a), getRankingInput(b));
      if (compared !== 0) return compared;
      return b.projectedScore - a.projectedScore;
    });

    let rank = 1;
    return entries.map((entry, index) => {
      const previous = entries[index - 1];
      const sharesRank = previous !== undefined &&
        compareScoreThenTiebreaker(getRankingInput(previous), getRankingInput(entry)) === 0;
      if (!sharesRank) rank = index + 1;
      return {
        ...entry,
        rank,
      };
    });
  }, [memberEntries, resolvedScoringSystem, includeLivePoints, viewMode]);

  const compactColumn = isCompactScreen
    ? resolveCompactPlayerColumn(
        sortedMembers.flatMap((entry) => getMemberNameLines(entry.memberUser)),
        selectedWeekOption?.displayLabel || 'Week 18',
      )
    : null;
  const playerColumnWidth = compactColumn?.playerColumnWidth ?? basePlayerColumnWidth;
  const compactWeekFontSize = compactColumn?.weekFontSize ?? 18;
  const identityHeaderWidth = rankColumnWidth + playerColumnWidth;
  const frozenColumnsWidth =
    identityHeaderWidth +
    (showPointsColumn ? pointsColumnWidth : correctColumnWidth);

  const currentUserIndex = useMemo(
    () => sortedMembers.findIndex((entry) => isCurrentUserMember(entry.memberUser)),
    [isCurrentUserMember, sortedMembers],
  );
  const scrollRequestKey = scrollToUserToken > 0 && selectedWeekId
    ? `${scrollToUserToken}:${selectedWeekId}:${viewMode}`
    : null;
  scrollRequestKeyRef.current = scrollRequestKey;
  const topScrollInset = Platform.OS === 'web' ? scoreHeaderHeight + 16 : 8;
  const bottomScrollInset = Platform.OS === 'web' ? 12 : Math.max(insets.bottom, 12) + 72;

  const scrollToCurrentUser = useCallback(() => {
    const requestKey = scrollRequestKeyRef.current;
    if (!requestKey || completedScrollKeyRef.current === requestKey || pendingScrollKeyRef.current === requestKey) {
      return;
    }

    const row = currentUserRowRef.current;
    const viewport = verticalViewportRef?.current ?? null;
    const scroller = verticalScrollRef?.current ?? null;
    if (!row || !viewport || !scroller) {
      return;
    }

    pendingScrollKeyRef.current = requestKey;
    let rowRect: WindowRect | null = null;
    let viewportRect: WindowRect | null = null;
    let remaining = 2;
    const finish = () => {
      remaining -= 1;
      if (remaining > 0) {
        return;
      }
      if (scrollRequestKeyRef.current !== requestKey) {
        if (pendingScrollKeyRef.current === requestKey) {
          pendingScrollKeyRef.current = null;
        }
        return;
      }
      if (!rowRect || !viewportRect || rowRect.height < 1 || viewportRect.height < 1) {
        pendingScrollKeyRef.current = null;
        return;
      }

      const currentScrollY = verticalScrollOffsetRef?.current ?? 0;
      const nextScrollY = getCurrentUserScoresScrollY({
        bottomInset: bottomScrollInset,
        currentScrollY,
        leadingContext: currentUserIndex > 0 ? rowRect.height : 0,
        rowHeight: rowRect.height,
        rowTopInViewport: rowRect.y - viewportRect.y,
        topInset: topScrollInset,
        viewportHeight: viewportRect.height,
      });
      if (Math.abs(nextScrollY - currentScrollY) > 1) {
        scroller.scrollTo({ animated: true, y: nextScrollY });
        assignRef(verticalScrollOffsetRef, nextScrollY);
      }
      pendingScrollKeyRef.current = null;
      completedScrollKeyRef.current = requestKey;
    };

    const measuredRow = measureInWindow(row, (rect) => {
      rowRect = rect;
      finish();
    });
    const measuredViewport = measureInWindow(viewport, (rect) => {
      viewportRect = rect;
      finish();
    });
    if (!measuredRow || !measuredViewport) {
      pendingScrollKeyRef.current = null;
    }
  }, [
    bottomScrollInset,
    currentUserIndex,
    topScrollInset,
    verticalScrollOffsetRef,
    verticalScrollRef,
    verticalViewportRef,
  ]);

  useEffect(() => {
    if (!scrollRequestKey || isLoading || currentUserIndex < 0) {
      return;
    }

    let secondFrame: number | null = null;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => {
        scrollToCurrentUser();
      });
    });

    return () => {
      cancelAnimationFrame(firstFrame);
      if (secondFrame !== null) {
        cancelAnimationFrame(secondFrame);
      }
    };
  }, [currentUserIndex, isLoading, scrollRequestKey, scrollToCurrentUser]);

  const weeklyTiebreakerOutcomeByUserId = useMemo(() => {
    const outcomes = new Map<string, 'up' | 'down'>();
    if (
      viewMode !== 'weekly' ||
      !weeklyTiebreakerColumn?.game.isCompleted ||
      typeof weeklyTiebreakerColumn.game.homeScore !== 'number' ||
      typeof weeklyTiebreakerColumn.game.awayScore !== 'number'
    ) {
      return outcomes;
    }

    const membersByScore = new Map<number, RankedMemberEntry[]>();
    sortedMembers.forEach((entry) => {
      const score = getWeeklyScoresRankingValue(entry, resolvedScoringSystem, includeLivePoints);
      const scoreGroup = membersByScore.get(score) ?? [];
      scoreGroup.push(entry);
      membersByScore.set(score, scoreGroup);
    });

    membersByScore.forEach((scoreGroup) => {
      // Tiebreakers only move standings within an existing score tie.
      if (scoreGroup.length < 2) return;
      const guessedMembers = scoreGroup.filter((entry) => Number.isFinite(entry.weeklyTiebreakerDelta));
      if (guessedMembers.length === 0) return;

      const closestDelta = Math.min(...guessedMembers.map((entry) => entry.weeklyTiebreakerDelta));
      const hasRankingMovement = scoreGroup.some((entry) => entry.weeklyTiebreakerDelta > closestDelta);
      if (!hasRankingMovement) return;

      guessedMembers.forEach((entry) => {
        const userId = entry.memberUser?.id;
        if (userId) {
          outcomes.set(userId, entry.weeklyTiebreakerDelta === closestDelta ? 'up' : 'down');
        }
      });
    });

    return outcomes;
  }, [includeLivePoints, resolvedScoringSystem, sortedMembers, viewMode, weeklyTiebreakerColumn]);

  const maxPossiblePoints = Number(getMaximumWeeklyPoints()) || 0;
  const teamLogoBaseUrl = 'https://a.espncdn.com/i/teamlogos/nfl/500';
  const weekPicker = (
    <AppSelect
      {...(selectedWeekOption?.id ? { value: selectedWeekOption.id } : {})}
      onChange={(value) => setSelectedWeekId(value)}
      options={seasonWeekOptions.map((week) => ({
        value: week.id,
        label: week.displayLabel,
      }))}
      placeholder={selectedWeekOption?.displayLabel || 'Select Week'}
      simplifiedTrigger
      triggerStyle={[
        styles.weekPickerTrigger,
        isCompactScreen && styles.weekPickerTriggerCompact,
      ]}
      valueStyle={[
        styles.weekPickerValue,
        isCompactScreen && styles.weekPickerValueCompact,
        isCompactScreen && {
          fontSize: compactWeekFontSize,
          lineHeight: compactWeekFontSize + 4,
        },
      ]}
    />
  );
  const weekPickerHeader = (
    <View
      style={[
        styles.weekPickerHeaderCell,
        styles.headerCell,
        isCompactScreen && styles.weekPickerHeaderCellCompact,
        { width: identityHeaderWidth, height: scoreHeaderHeight },
      ]}
      testID="scores-week-picker"
    >
      {weekPicker}
    </View>
  );

  if (isLoading) {
    return (
      <Card style={styles.transparentCard}>
        <CardContent padding="none">
          <Box gap="m">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} height={48} borderRadius={8} />
            ))}
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!leagueMembers || leagueMembers.length === 0) {
    return (
      <Card style={styles.transparentCard}>
        <CardContent padding="none">
          <Box alignItems="center" paddingVertical="xl">
            <Text variant="body" color="mutedForeground">No league members found.</Text>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card style={styles.transparentCard}>
      <CardContent style={styles.contentNoPadding} padding="none">
        {betColumns.length === 0 ? (
          <View>
            <View style={[styles.emptyWeekPickerRow, { height: scoreHeaderHeight }]}>
              {weekPickerHeader}
            </View>
            <View style={styles.emptyContainer}>
              <Text style={styles.emptyText}>
                No games available for {selectedWeekOption?.displayLabel || 'this week'}
              </Text>
            </View>
          </View>
        ) : (
          <View style={styles.gridWrapper}>
            {seasonWeekMode === 'propWeeklyOnly' && (
              <View style={styles.viewModeToggle}>
                <Pressable
                  onPress={() => setViewMode('weekly')}
                  style={[
                    styles.viewModeButton,
                    viewMode === 'weekly' && styles.viewModeButtonActive,
                  ]}
                >
                  <Text
                    style={[
                      styles.viewModeButtonText,
                      viewMode === 'weekly' && styles.viewModeButtonTextActive,
                    ]}
                  >
                    Weekly Props
                  </Text>
                </Pressable>
                <Pressable
                  onPress={() => setViewMode('season')}
                  style={[
                    styles.viewModeButton,
                    viewMode === 'season' && styles.viewModeButtonActive,
                  ]}
                >
                  <Text
                    style={[
                      styles.viewModeButtonText,
                      viewMode === 'season' && styles.viewModeButtonTextActive,
                    ]}
                  >
                    Season Total
                  </Text>
                </Pressable>
              </View>
            )}
            {hasExclusions && seasonWeekMode !== 'propWeeklyOnly' && (
              <View style={styles.viewModeToggle}>
                <Pressable
                  onPress={() => setViewMode('weekly')}
                  style={[
                    styles.viewModeButton,
                    viewMode === 'weekly' && styles.viewModeButtonActive,
                  ]}
                >
                  <Text
                    style={[
                      styles.viewModeButtonText,
                      viewMode === 'weekly' && styles.viewModeButtonTextActive,
                    ]}
                  >
                    Weekly
                  </Text>
                </Pressable>
                <Pressable
                  onPress={() => setViewMode('season')}
                  style={[
                    styles.viewModeButton,
                    viewMode === 'season' && styles.viewModeButtonActive,
                  ]}
                >
                  <Text
                    style={[
                      styles.viewModeButtonText,
                      viewMode === 'season' && styles.viewModeButtonTextActive,
                    ]}
                  >
                    Season
                  </Text>
                </Pressable>
              </View>
            )}
            <View style={styles.gridContainer}>
              <View style={[styles.frozenPlayerColumn, { width: frozenColumnsWidth }]}>
                <View style={Platform.OS === 'web' ? stickyScoreHeaderStyle : undefined}>
                <View style={[styles.gridRow, styles.headerRow, styles.frozenHeaderRow, { height: scoreHeaderHeight }]}>
                  {weekPickerHeader}
                  {showPointsColumn ? (
                    <View
                      style={[
                        styles.cellPoints,
                        styles.headerCell,
                        styles.pointsHeaderButton,
                        { width: pointsColumnWidth, height: scoreHeaderHeight },
                      ]}
                      testID="scores-points-column-header"
                    >
                      <Pressable
                        accessibilityRole="button"
                        accessibilityLabel={pointsColumnShowsCorrect ? 'Show points' : 'Show correct picks'}
                        accessibilityState={{ selected: pointsColumnShowsCorrect }}
                        onPress={() => setPointsColumnShowsCorrect((prev) => !prev)}
                        style={({ pressed }) => [
                          styles.metricHeaderControl,
                          pressed && styles.pointsHeaderPressed,
                        ]}
                        testID="scores-metric-toggle"
                      >
                        <Text
                          numberOfLines={1}
                          style={[
                            styles.headerText,
                            isWideScreen && styles.headerTextWide,
                          ]}
                        >
                          {pointsColumnShowsCorrect ? 'Correct' : 'Pts'}
                        </Text>
                      </Pressable>
                      <Pressable
                        accessibilityRole="button"
                        accessibilityLabel={
                          pointsColumnShowsCorrect
                            ? 'Toggle correct between live and final'
                            : 'Toggle points between live and final'
                        }
                        accessibilityState={{ checked: includeLivePoints }}
                        onPress={() => setIncludeLivePoints((prev) => !prev)}
                        style={({ pressed }) => [
                          styles.metricHeaderControl,
                          pressed && styles.pointsHeaderPressed,
                        ]}
                        testID="scores-live-toggle"
                      >
                        <Text style={styles.pointsModeText}>
                          {includeLivePoints ? 'Live' : 'Final'}
                        </Text>
                      </Pressable>
                    </View>
                  ) : (
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel="Toggle correct between live and final"
                      accessibilityState={{ checked: includeLivePoints }}
                      onPress={() => setIncludeLivePoints((prev) => !prev)}
                      style={({ pressed }) => [
                        styles.cellCorrect,
                        styles.headerCell,
                        styles.pointsHeaderButton,
                        { width: correctColumnWidth, height: scoreHeaderHeight },
                        pressed && styles.pointsHeaderPressed,
                      ]}
                    >
                      <Text style={[styles.headerText, isWideScreen && styles.headerTextWide]}>Correct</Text>
                      {!isWideScreen && <Text style={styles.pointsModeText}>{includeLivePoints ? 'Live' : 'Final'}</Text>}
                    </Pressable>
                  )}
                </View>
                {Platform.OS === 'web' && <View style={styles.horizontalScrollbarGutter} />}
                </View>

                {showWinningPointsRow && (
                  <View style={[styles.gridRow, { height: winningPointsRowHeight }]}>
                    <View style={[styles.gridCell, styles.winningPointsCell, { width: frozenColumnsWidth, height: winningPointsRowHeight }]}>
                      <Text style={styles.winningPointsText}>Points</Text>
                    </View>
                  </View>
                )}

                {sortedMembers.map((entry, index) => {
                  const memberUser = entry.memberUser;
                  const isCurrentUser = isCurrentUserMember(memberUser);
                  const nameLines = getMemberNameLines(memberUser);
                  const stackedName = isCompactScreen && nameLines.length > 1;

                  return (
                    <View
                      key={`player-${memberUser?.id || index}`}
                      collapsable={false}
                      onLayout={isCurrentUser ? () => {
                        requestAnimationFrame(() => {
                          requestAnimationFrame(() => {
                            scrollToCurrentUser();
                          });
                        });
                      } : undefined}
                      ref={isCurrentUser ? currentUserRowRef : undefined}
                      style={[
                        styles.gridRow,
                        styles.dataRow,
                        isWideScreen && styles.wideDataRow,
                        styles.frozenDataRow,
                        index % 2 === 1 && styles.zebraRow,
                        isCurrentUser && styles.currentUserRow,
                        { height: playerRowHeight },
                      ]}
                      testID={isCurrentUser ? 'scores-current-user-row' : undefined}
                    >
                      {isCurrentUser && <View pointerEvents="none" style={styles.currentUserIndicator} />}
                      <View
                        style={[
                          styles.cellRank,
                          styles.gridCell,
                          index % 2 === 1 && styles.zebraCell,
                          isCurrentUser && styles.currentUserRankCell,
                          styles.memberCellDivider,
                          { width: rankColumnWidth, height: playerRowHeight },
                        ]}
                      >
                        <Text style={[styles.rankText, isWideScreen && styles.wideRankText, isCurrentUser && styles.currentUserRankText]}>
                          #{entry.rank}
                        </Text>
                      </View>
                      <View
                        style={[
                          styles.cellPlayer,
                          styles.gridCell,
                          index % 2 === 1 && styles.zebraCell,
                          isCurrentUser && styles.currentUserCell,
                          styles.memberCellDivider,
                          isCompactScreen && styles.cellPlayerCompact,
                          { width: playerColumnWidth, height: playerRowHeight },
                        ]}
                      >
                        <Text
                          numberOfLines={isCompactScreen ? 2 : 1}
                          style={[
                            styles.playerText,
                            isWideScreen && styles.widePlayerText,
                            isCurrentUser && styles.currentUserPlayerText,
                            isCompactScreen && styles.playerTextCompact,
                            stackedName && styles.playerNameLine,
                          ]}
                        >
                          {stackedName ? nameLines.join('\n') : nameLines.join(' ')}
                        </Text>
                      </View>
                      {showPointsColumn ? (() => {
                        const pointsValue = includeLivePoints ? entry.projectedScore : entry.baseScore;
                        const formattedPoints =
                          resolvedScoringSystem === 'oddsBased'
                            ? Number(pointsValue).toFixed(1)
                            : Math.round(pointsValue).toString();
                        const correctStats = includeLivePoints ? entry.projectedGamesCorrect : entry.gamesCorrect;
                        return (
                          <View
                            style={[
                              styles.cellPoints,
                              styles.gridCell,
                              index % 2 === 1 && styles.zebraCell,
                              isCurrentUser && styles.currentUserCell,
                              styles.memberCellDivider,
                              isCompactScreen && !pointsColumnShowsCorrect && styles.metricCellCompact,
                              { width: pointsColumnWidth, height: playerRowHeight },
                            ]}
                          >
                            <Pressable
                              accessibilityRole="button"
                              accessibilityLabel={pointsColumnShowsCorrect ? 'Show points' : 'Show correct picks'}
                              onPress={() => setPointsColumnShowsCorrect((prev) => !prev)}
                              style={({ pressed }) => [
                                styles.metricCellButton,
                                isCompactScreen && !pointsColumnShowsCorrect && styles.metricCellButtonCompact,
                                pressed && styles.pointsHeaderPressed,
                              ]}
                              testID={index === 0 ? 'scores-metric-cell' : undefined}
                            >
                              {pointsColumnShowsCorrect ? (
                                <View style={styles.correctValue}>
                                  <Text style={styles.correctText}>{correctStats.correct}</Text>
                                  <Text style={styles.correctTotalText}>/{correctStats.total}</Text>
                                </View>
                              ) : (
                                <Text style={styles.pointsText}>{formattedPoints}</Text>
                              )}
                            </Pressable>
                          </View>
                        );
                      })() : (
                        <View
                          style={[
                            styles.cellCorrect,
                            styles.gridCell,
                            index % 2 === 1 && styles.zebraCell,
                            isCurrentUser && styles.currentUserCell,
                            styles.memberCellDivider,
                            isCompactScreen && styles.metricCellCompact,
                            { width: correctColumnWidth, height: playerRowHeight },
                          ]}
                        >
                          <View style={styles.correctValue}>
                            <Text style={styles.correctText}>
                              {(includeLivePoints ? entry.projectedGamesCorrect : entry.gamesCorrect).correct}
                            </Text>
                            <Text style={styles.correctTotalText}>
                              /{(includeLivePoints ? entry.projectedGamesCorrect : entry.gamesCorrect).total}
                            </Text>
                          </View>
                        </View>
                      )}
                    </View>
                  );
                })}
              </View>

              <View style={styles.scrollableScorePane}>
                <View style={Platform.OS === 'web' ? stickyScoreHeaderStyle : undefined}>
                <View style={[styles.scrollHeaderViewport, { height: scoreHeaderHeight }]}>
                  <ScrollView
                    ref={headerScrollRef}
                    horizontal
                    nestedScrollEnabled
                    showsHorizontalScrollIndicator={false}
                    decelerationRate={isCompactScreen ? 'fast' : 'normal'}
                    style={styles.scrollableScoreHeader}
                    contentContainerStyle={styles.scrollableScoreContent}
                    onScroll={(event) => syncHorizontalScroll('header', event)}
                    scrollEventThrottle={16}
                  >
                  <View>
                  <View style={[styles.gridRow, styles.headerRow, styles.scrollHeaderRow, { height: scoreHeaderHeight }]}>
                  {betColumns.map((column, columnIndex) => {
                    const { bet, game } = column;
                    const nextColumn = betColumns[columnIndex + 1];
                    const sharesGameHeaderWithTiebreaker =
                      Boolean(
                        nextColumn &&
                          nextColumn.game.id === game.id &&
                          nextColumn.bet.betType === 'total_tiebreaker',
                      );
                    const isTeamMarket = bet.betType === 'moneyline' || bet.betType === 'spread';
                    const spreadValue = bet.displayInfo?.spread;
                    const parsedSpread = typeof spreadValue === 'number'
                      ? spreadValue
                      : typeof spreadValue === 'string'
                        ? Number.parseFloat(spreadValue)
                        : Number.NaN;
                    const rawSpread = Number.isFinite(parsedSpread) ? parsedSpread : null;
                    const awayMarketValue = bet.betType === 'moneyline'
                      ? formatOdds(bet.oddsTwo ?? game.awayOdds)
                      : bet.betType === 'spread'
                        ? formatSpread(rawSpread === null ? null : -rawSpread)
                        : '';
                    const homeMarketValue = bet.betType === 'moneyline'
                      ? formatOdds(bet.oddsOne ?? game.homeOdds)
                      : bet.betType === 'spread'
                        ? formatSpread(rawSpread)
                        : '';
                    const awayScore = typeof game.awayScore === 'number' ? game.awayScore : null;
                    const homeScore = typeof game.homeScore === 'number' ? game.homeScore : null;
                    const label = bet.betType === 'spread' ? 'Spread' : formatBetLabel(bet);
                    const description = resolveDetailedBetDescription(bet, propDefinitionById);
                    const isPickOfTheWeek = bet.betType === 'custom_prop' && isPropOfTheWeekBet(bet);
                    const propDefinition = bet.propDefinitionId
                      ? propDefinitionById.get(bet.propDefinitionId)
                      : undefined;
                    const propEvaluation = bet.propDefinitionId
                      ? propEvaluationByDefinitionId.get(bet.propDefinitionId)
                      : undefined;
                    const propHeaderRows = isPickOfTheWeek && propDefinition
                      ? formatPropStatHeaderRows({
                        propType: propDefinition.propType,
                        threshold: propDefinition.threshold,
                        displayConfig: propDefinition.displayConfig,
                        players: propDefinition.players,
                      }, propEvaluation ? toNumericRecord(propEvaluation.currentValues) : null)
                      : null;
                    const supportingResult = isPickOfTheWeek
                      ? null
                      : bet.betType === 'total' || bet.betType === 'total_tiebreaker'
                        ? formatBetOutcome(game, bet)
                        : bet.betType === 'custom_prop'
                          ? formatPropStatLine(bet) ?? formatBetOutcome(game, bet)
                          : null;
                    const tooltipContent = (
                      <View>
                        <Text style={styles.tooltipTitle}>{label}</Text>
                        {Boolean(description) && (
                          <Text style={styles.tooltipDescription}>{description}</Text>
                        )}
                      </View>
                    );

                    const renderTeamRow = (
                      team: Team,
                      score: number | null,
                      marketValue: string,
                    ) => (
                      <View
                        accessibilityLabel={`${team.abbreviation}${score === null ? '' : ` ${score}`}${marketValue ? ` ${marketValue}` : ''}`}
                        key={team.id}
                        style={styles.headerTeamRow}
                      >
                        {team.logoUrl === null ? (
                          <View
                            style={[
                              styles.teamLogo,
                              styles.teamLogoFallback,
                              isCompactScreen && styles.teamLogoCompact,
                              isWideScreen && styles.teamLogoWide,
                            ]}
                          >
                            <Text style={styles.teamLogoFallbackText}>{team.abbreviation}</Text>
                          </View>
                        ) : (
                          <Image
                            source={{ uri: team.logoUrl ?? `${teamLogoBaseUrl}/${team.abbreviation.toLowerCase()}.png` }}
                            style={[styles.teamLogo, isCompactScreen && styles.teamLogoCompact, isWideScreen && styles.teamLogoWide]}
                            resizeMode="contain"
                          />
                        )}
                        {Boolean(marketValue) && (
                          <Text
                            style={[styles.headerTeamMarket, isCompactScreen && styles.headerTeamMarketCompact]}
                            numberOfLines={1}
                          >
                            {marketValue}
                          </Text>
                        )}
                        <View style={[styles.headerTeamScoreSlot, isCompactScreen && styles.headerTeamScoreSlotCompact]}>
                          {score !== null && (
                            <Text style={[styles.headerTeamScore, isCompactScreen && styles.headerTeamScoreCompact]}>
                              {score}
                            </Text>
                          )}
                        </View>
                      </View>
                    );

                    return (
                      <View
                        key={column.betKey}
                        testID={isPickOfTheWeek ? 'scores-potw-header' : undefined}
                        style={[
                          styles.cellGame,
                          styles.headerCell,
                          !sharesGameHeaderWithTiebreaker && styles.gameColumnDivider,
                          isCompactScreen && styles.cellGameCompact,
                          { height: scoreHeaderHeight, width: gameColumnWidth },
                        ]}
                      >
                        <View style={[styles.gameHeader, isCompactScreen && styles.gameHeaderCompact]}>
                          {!isTeamMarket && (
                            <Tooltip content={tooltipContent}>
                              <View style={[styles.betTag, isCompactScreen && styles.betTagCompact]}>
                                <Text style={[styles.betTagText, isCompactScreen && styles.betTagTextCompact]} numberOfLines={1}>
                                  {label}
                                </Text>
                              </View>
                            </Tooltip>
                          )}
                          {isPickOfTheWeek ? (
                            <View style={[styles.headerTeams, styles.headerPropTeams]}>
                              {(propHeaderRows ?? []).map((row) => (
                                <View
                                  accessibilityLabel={`${row.label} ${row.value}`}
                                  key={row.id}
                                  style={styles.headerTeamRow}
                                >
                                  <Text
                                    numberOfLines={1}
                                    style={[styles.headerPropLabel, isCompactScreen && styles.headerPropLabelCompact]}
                                  >
                                    {row.label}
                                  </Text>
                                  <View style={styles.headerPropValueSlot}>
                                    <Text style={[styles.headerTeamScore, isCompactScreen && styles.headerTeamScoreCompact]}>
                                      {row.value}
                                    </Text>
                                  </View>
                                </View>
                              ))}
                            </View>
                          ) : bet.betType !== 'total_tiebreaker' ? (
                            <View style={[styles.headerTeams, isCompactScreen && styles.headerTeamsCompact]}>
                              {renderTeamRow(game.awayTeam, awayScore, awayMarketValue)}
                              {renderTeamRow(game.homeTeam, homeScore, homeMarketValue)}
                            </View>
                          ) : null}
                          {Boolean(supportingResult) && (
                            <Text style={[styles.scoreText, isCompactScreen && styles.scoreTextCompact]} numberOfLines={1}>
                              {supportingResult}
                            </Text>
                          )}
                          <Text style={[styles.gameStatusText, isCompactScreen && styles.gameStatusTextCompact]}>
                            {formatGameStatusLabel(game)}
                          </Text>
                        </View>
                      </View>
                    );
                  })}
                  </View>
                    </View>
                  </ScrollView>
                </View>
                <ScoresHorizontalScrollbar
                  contentWidth={scrollableContentWidth}
                  onScroll={(event) => syncHorizontalScroll('top', event)}
                  scrollRef={topScrollbarRef}
                  sticky="top"
                  testID="scores-horizontal-scrollbar-top"
                />
                </View>

                <ScrollView
                  ref={bodyScrollRef}
                  horizontal
                  nestedScrollEnabled
                  showsHorizontalScrollIndicator={Platform.OS !== 'web'}
                  decelerationRate={isCompactScreen ? 'fast' : 'normal'}
                  style={styles.scrollableScoreBody}
                  contentContainerStyle={styles.scrollableScoreContent}
                  onContentSizeChange={(width) => {
                    if (width > 0) {
                      setMeasuredScrollableWidth((current) => (
                        Math.abs(current - width) > 1 ? width : current
                      ));
                    }
                  }}
                  onScroll={(event) => syncHorizontalScroll('body', event)}
                  scrollEventThrottle={16}
                >
                  <View>
                {showWinningPointsRow && <View style={[styles.gridRow, { height: winningPointsRowHeight }]}>
                  {betColumns.map((column) => (
                    <View
                      key={'points-' + column.betKey}
                      style={[
                        styles.cellGame,
                        styles.gridCell,
                        styles.winningPointsCell,
                        styles.gameColumnDivider,
                        isCompactScreen && styles.cellGameCompact,
                        { height: winningPointsRowHeight, width: gameColumnWidth },
                      ]}
                    >
                      <Text style={styles.winningPointsText}>
                        {getWinningPoints(column.game, column.bet)}
                      </Text>
                    </View>
                  ))}
                </View>}

                {sortedMembers.map((entry, index) => {
                  const memberUser = entry.memberUser;
                  const isCurrentUser = isCurrentUserMember(memberUser);
                  const userId = memberUser?.id || '';

                  return (
                    <View
                      key={`score-${memberUser?.id || index}`}
                      style={[
                        styles.gridRow,
                        styles.dataRow,
                        styles.scrollDataRow,
                        isWideScreen && styles.wideDataRow,
                        index % 2 === 1 && styles.zebraRow,
                        isCurrentUser && styles.currentUserRow,
                        { height: playerRowHeight },
                      ]}
                    >
                      {betColumns.map((column) => {
                        const status = getPickStatus(userId, column);
                        const userPick = picksByUserAndBet.get(userId)?.get(column.betKey);
                        const selectedTeamId = userPick?.selectedTeamId ?? null;
                        const showPick = shouldShowPick(userId, column.game);
                        const liveState = doesUserLeadGame(column.bet, column.game, userPick);
                        const propEvaluation = column.bet.propDefinitionId
                          ? propEvaluationByDefinitionId.get(column.bet.propDefinitionId)
                          : null;
                        const inProgress =
                          column.bet.betType === 'custom_prop'
                            ? propEvaluation?.status === 'IN_PROGRESS'
                            : isGameInProgress(column.game);

                        const cellStyle: StyleProp<ViewStyle>[] = [
                          styles.cellGame,
                          isCompactScreen && styles.cellGameCompact,
                          index % 2 === 1 && styles.zebraCell,
                          isCurrentUser && styles.currentUserCell,
                        ];
                        const pickStyle: StyleProp<ViewStyle>[] = [styles.pickCell];
                        let content = null;

                        if (column.bet.betType === 'total_tiebreaker') {
                          const guess = userPick?.details?.tiebreakerGuess;
                          const tiebreakerOutcome = column.betKey === weeklyTiebreakerColumn?.betKey
                            ? weeklyTiebreakerOutcomeByUserId.get(userId)
                            : undefined;
                          if (!userPick) {
                            pickStyle.push(styles.pickCellEmpty);
                            content = <Text style={styles.pickTextEmpty}>—</Text>;
                          } else if (!showPick) {
                            pickStyle.push(styles.pickCellHidden);
                            content = <EyeOff size={12} color={colors.text.secondary} />;
                          } else if (typeof guess !== 'number') {
                            pickStyle.push(styles.pickCellEmpty);
                            content = <Text style={styles.pickTextEmpty}>—</Text>;
                          } else if (tiebreakerOutcome === 'up') {
                            pickStyle.push(styles.pickCellTiebreaker);
                            content = (
                              <View style={styles.pickContent}>
                                <ChevronUp size={13} color={colors.success[700]} />
                                <Text style={styles.pickTextTiebreaker}>{guess}</Text>
                              </View>
                            );
                          } else if (tiebreakerOutcome === 'down') {
                            pickStyle.push(styles.pickCellTiebreaker);
                            content = (
                              <View style={styles.pickContent}>
                                <ChevronDown size={13} color={colors.error[700]} />
                                <Text style={styles.pickTextTiebreaker}>{guess}</Text>
                              </View>
                            );
                          } else {
                            pickStyle.push(styles.pickCellTiebreaker);
                            content = <Text style={styles.pickTextTiebreaker}>{guess}</Text>;
                          }
                        } else if (!status) {
                          pickStyle.push(styles.pickCellEmpty);
                          content = <Text style={styles.pickTextEmpty}>—</Text>;
                        } else if (!showPick) {
                          pickStyle.push(styles.pickCellHidden);
                          content = <EyeOff size={12} color={colors.text.secondary} />;
                        } else if (status === 'pending') {
                          const side = userPick ? getSideFromPick(userPick, column.game) : null;
                          const teamLabel =
                            column.bet.betType === 'total'
                              ? side === 'one'
                                ? 'Over'
                                : 'Under'
                              : column.bet.betType === 'custom_prop'
                                ? side
                                  ? resolveCustomPropSideLabel(column.bet, side)
                                  : '?'
                              : selectedTeamId === column.game.homeTeam.id
                                ? column.game.homeTeam.abbreviation
                                : column.game.awayTeam.abbreviation;
                          if (inProgress && liveState === 'leading') {
                            pickStyle.push(styles.pickCellPendingLeading);
                            content = (
                              <View style={styles.pickContent}>
                                <Check size={12} color={colors.success[700]} />
                                <Text style={styles.pickTextPendingLeading}>{teamLabel}</Text>
                              </View>
                            );
                          } else if (inProgress && liveState === 'trailing') {
                            pickStyle.push(styles.pickCellPendingTrailing);
                            content = (
                              <View style={styles.pickContent}>
                                <X size={12} color={colors.error[700]} />
                                <Text style={styles.pickTextPendingTrailing}>{teamLabel}</Text>
                              </View>
                            );
                          } else {
                            pickStyle.push(styles.pickCellPending);
                            content = <Text style={styles.pickTextPending}>{teamLabel}</Text>;
                          }
                        } else if (status === 'correct') {
                          pickStyle.push(styles.pickCellCorrect);
                          const side = userPick ? getSideFromPick(userPick, column.game) : null;
                          const team =
                            column.bet.betType === 'total'
                              ? side === 'one'
                                ? 'Over'
                                : 'Under'
                              : column.bet.betType === 'custom_prop'
                                ? side
                                  ? resolveCustomPropSideLabel(column.bet, side)
                                  : '?'
                              : selectedTeamId === column.game.homeTeam.id
                                ? column.game.homeTeam.abbreviation
                                : column.game.awayTeam.abbreviation;
                          content = (
                            <View style={styles.pickContent}>
                              <Check size={12} color={colors.success[700]} />
                              <Text style={styles.pickTextCorrect}>{team}</Text>
                            </View>
                          );
                        } else if (status === 'push') {
                          pickStyle.push(styles.pickCellPush);
                          const side = userPick ? getSideFromPick(userPick, column.game) : null;
                          const team =
                            column.bet.betType === 'total'
                              ? side === 'one'
                                ? 'Over'
                                : 'Under'
                              : column.bet.betType === 'custom_prop'
                                ? side
                                  ? resolveCustomPropSideLabel(column.bet, side)
                                  : '?'
                                : selectedTeamId === column.game.homeTeam.id
                                  ? column.game.homeTeam.abbreviation
                                  : column.game.awayTeam.abbreviation;
                          content = (
                            <View style={styles.pickContent}>
                              <Text style={styles.pickTextPush}>{team}</Text>
                            </View>
                          );
                        } else {
                          pickStyle.push(styles.pickCellIncorrect);
                          const side = userPick ? getSideFromPick(userPick, column.game) : null;
                          const team =
                            column.bet.betType === 'total'
                              ? side === 'one'
                                ? 'Over'
                                : 'Under'
                              : column.bet.betType === 'custom_prop'
                                ? side
                                  ? resolveCustomPropSideLabel(column.bet, side)
                                  : '?'
                              : selectedTeamId === column.game.homeTeam.id
                                ? column.game.homeTeam.abbreviation
                                : column.game.awayTeam.abbreviation;
                          content = (
                            <View style={styles.pickContent}>
                              <X size={12} color={colors.error[700]} />
                              <Text style={styles.pickTextIncorrect}>{team}</Text>
                            </View>
                          );
                        }

                        cellStyle.push(styles.memberCellDivider, styles.gameColumnDivider, { height: playerRowHeight, width: gameColumnWidth });
                        return (
                          <View key={`${memberUser?.id || index}-${column.betKey}`} style={cellStyle}>
                            <View style={pickStyle}>{content}</View>
                          </View>
                        );
                      })}
                    </View>
                  );
                })}
              </View>
              </ScrollView>
              <ScoresHorizontalScrollbar
                contentWidth={scrollableContentWidth}
                onScroll={(event) => syncHorizontalScroll('bottom', event)}
                scrollRef={bottomScrollbarRef}
                sticky="bottom"
                testID="scores-horizontal-scrollbar-bottom"
              />
            </View>
          </View>
        </View>
        )}

        {betColumns.length > 0 && (
          <View style={styles.gridFooter}>
            <Text style={styles.gridFooterText}>{betColumns.length} {betColumns.length === 1 ? 'game' : 'games'} this week</Text>
            <Text style={styles.gridFooterHint}>SWIPE TO VIEW MORE →</Text>
          </View>
        )}

        {seasonWeekOptions.length > 0 && !isCompactScreen && (
          <View style={styles.legend}>
            <View style={styles.legendRow}>
              <View style={styles.legendItem}>
                <View style={[styles.legendBox, styles.legendCorrect]} />
                <Text style={styles.legendText}>Correct</Text>
              </View>
              <View style={styles.legendItem}>
                <View style={[styles.legendBox, styles.legendIncorrect]} />
                <Text style={styles.legendText}>Incorrect</Text>
              </View>
              <View style={styles.legendItem}>
                <View style={[styles.legendBox, styles.legendPush]} />
                <Text style={styles.legendText}>Push</Text>
              </View>
              <View style={styles.legendItem}>
                <View style={[styles.legendBox, styles.legendPending]} />
                <Text style={styles.legendText}>Pending</Text>
              </View>
              {hasLiveGames && (
                <>
                  <View style={styles.legendItem}>
                    <View style={[styles.legendBox, styles.legendPendingLeading]} />
                    <Text style={styles.legendText}>In Progress (Leading)</Text>
                  </View>
                  <View style={styles.legendItem}>
                    <View style={[styles.legendBox, styles.legendPendingTrailing]} />
                    <Text style={styles.legendText}>In Progress (Trailing)</Text>
                  </View>
                </>
              )}
              <View style={styles.legendItem}>
                <EyeOff size={12} color={colors.text.secondary} />
                <Text style={styles.legendText}>Hidden</Text>
              </View>
            </View>
            {resolvedScoringSystem === 'oddsBased' && (
              <View style={styles.perfectWeekBadge}>
                <Text style={styles.perfectWeekLabel}>Perfect Week Points (Completed Games):</Text>
                <Text style={styles.perfectWeekValue}>{Number(maxPossiblePoints || 0).toFixed(1)}</Text>
              </View>
            )}
          </View>
        )}
      </CardContent>
    </Card>
  );
}

const styles = StyleSheet.create(() => ({
  betTag: {
    alignSelf: 'center',
    backgroundColor: colors.ink.acid,
    borderColor: colors.ink.press,
    borderRadius: 0,
    borderWidth: 1,
    marginBottom: 2,
    marginTop: 2,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  betTagCompact: {
    marginBottom: 1,
    marginTop: 1,
    paddingHorizontal: 4,
    paddingVertical: 1,
  },
  betTagText: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 10,
    fontWeight: '700',
    textAlign: 'center',
    textTransform: 'uppercase',
  },
  betTagTextCompact: {
    fontSize: 8,
  },
  cellCorrect: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 0,
    width: 64,
  },
  cellGame: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 2,
    width: 108,
  },
  cellGameCompact: {
    paddingHorizontal: 1,
  },
  cellPlayer: {
    justifyContent: 'center',
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  cellPlayerCompact: {
    overflow: 'hidden',
    paddingHorizontal: 2,
    paddingVertical: 1,
  },
  cellPoints: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 0,
    width: 66,
  },
  cellRank: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 0,
    width: 36,
  },
  contentNoPadding: {
    paddingHorizontal: 0,
  },
  correctText: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 22,
    fontVariant: ['tabular-nums'],
    fontWeight: '700',
    lineHeight: 24,
  },
  correctTotalText: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 12,
    fontVariant: ['tabular-nums'],
    fontWeight: '600',
  },
  correctValue: {
    alignItems: 'baseline',
    flexDirection: 'row',
  },
  currentUserCell: {
    backgroundColor: colors.warning[200],
  },
  currentUserIndicator: {
    backgroundColor: colors.ink.maroon,
    bottom: 0,
    left: 0,
    position: 'absolute',
    top: 0,
    width: 4,
    zIndex: 3,
  },
  currentUserPlayerText: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.bodyMedium,
    fontWeight: '700',
  },
  currentUserRankCell: {
    backgroundColor: colors.warning[200],
  },
  currentUserRankText: {
    color: colors.text.primary,
  },
  currentUserRow: {
    backgroundColor: colors.warning[200],
    position: 'relative',
  },
  dataRow: {
    backgroundColor: colors.ink.paper,
    borderBottomColor: colors.neutral[200],
    borderBottomWidth: 1,
    borderRadius: 0,
    marginBottom: 0,
    overflow: 'hidden',
  },
  emptyContainer: {
    alignItems: 'center',
    paddingVertical: 32,
  },
  emptyText: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.body,
    fontSize: 14,
    marginTop: 16,
  },
  emptyWeekPickerRow: {
    alignItems: 'stretch',
    backgroundColor: colors.ink.paper,
    flexDirection: 'row',
  },
  frozenDataRow: {
    borderBottomRightRadius: 0,
    borderTopRightRadius: 0,
  },
  frozenHeaderRow: {
    borderBottomRightRadius: 0,
    borderTopRightRadius: 0,
    marginBottom: 0,
  },
  frozenPlayerColumn: {
    backgroundColor: colors.ink.paper,
    borderRightColor: colors.neutral[300],
    borderRightWidth: 1,
    flexShrink: 0,
    shadowColor: colors.ink.press,
    shadowOffset: { width: 5, height: 0 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    zIndex: 2,
  },
  gameColumnDivider: {
    borderRightColor: colors.neutral[200],
    borderRightWidth: 1,
  },
  gameHeader: {
    alignItems: 'center',
    gap: 1,
    width: '100%',
  },
  gameHeaderCompact: {
    gap: 0,
  },
  gameStatusText: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 9,
    fontWeight: '600',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
  },
  gameStatusTextCompact: {
    fontSize: 8,
    lineHeight: 10,
  },
  gridCell: {
    backgroundColor: colors.ink.paper,
  },
  gridContainer: {
    backgroundColor: colors.ink.paper,
    flexDirection: 'row',
    paddingLeft: 0,
    paddingRight: 0,
    paddingVertical: 0,
  },
  gridFooter: {
    alignItems: 'center',
    borderTopColor: colors.neutral[200],
    borderTopWidth: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  gridFooterHint: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 9,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  gridFooterText: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 10,
    textTransform: 'uppercase',
  },
  gridRow: {
    flexDirection: 'row',
    marginBottom: 0,
  },
  gridWrapper: {
    backgroundColor: colors.ink.paper,
    paddingVertical: 0,
  },
  headerCell: {
    backgroundColor: colors.ink.paper,
    borderBottomColor: colors.neutral[300],
    borderBottomWidth: 1,
    borderColor: transparentColor,
    borderRadius: 0,
  },
  headerPropLabel: {
    color: colors.text.primary,
    flex: 1,
    fontFamily: typography.fontFamily.bodyMedium,
    fontSize: 11,
    minWidth: 0,
  },
  headerPropLabelCompact: {
    fontSize: 10,
  },
  headerPropTeams: {
    width: '100%',
  },
  headerPropValueSlot: {
    alignItems: 'flex-end',
    flexShrink: 0,
    justifyContent: 'center',
    marginLeft: 2,
    minWidth: 16,
  },
  headerRow: {
    borderRadius: 0,
    overflow: 'visible',
  },
  headerTeamMarket: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 9,
    fontWeight: '600',
    marginLeft: 2,
    textAlign: 'left',
    width: 28,
  },
  headerTeamMarketCompact: {
    fontSize: 8,
    width: 22,
  },
  headerTeamRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 2,
    justifyContent: 'flex-start',
    minHeight: 25,
    width: '100%',
  },
  headerTeamScore: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 13,
    fontWeight: '700',
    textAlign: 'right',
    width: '100%',
  },
  headerTeamScoreCompact: {
    fontSize: 11,
  },
  headerTeamScoreSlot: {
    alignItems: 'flex-end',
    justifyContent: 'center',
    marginLeft: 'auto',
    width: 20,
  },
  headerTeamScoreSlotCompact: {
    width: 16,
  },
  headerTeams: {
    alignSelf: 'center',
    width: 100,
  },
  headerTeamsCompact: {
    width: 72,
  },
  headerText: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.bodyMedium,
    fontSize: 11,
    fontWeight: '600',
    letterSpacing: 0.2,
    textAlign: 'center',
    textTransform: 'uppercase',
  },
  headerTextWide: {
    fontSize: 12,
  },
  horizontalScrollbarFill: {
    height: 1,
  },
  horizontalScrollbarGutter: {
    backgroundColor: colors.ink.paper,
    height: 16,
    width: '100%',
  },
  horizontalScrollbarTrack: {
    backgroundColor: colors.ink.paper,
    height: 16,
    maxHeight: 16,
    width: '100%',
  },
  legend: {
    borderTopColor: colors.ink.press,
    borderTopWidth: 2,
    marginTop: 16,
    paddingTop: 16,
  },
  legendBox: {
    borderColor: colors.ink.press,
    borderRadius: 0,
    borderWidth: 1,
    height: 12,
    width: 12,
  },
  legendCorrect: {
    backgroundColor: colors.state.successBg,
  },
  legendIncorrect: {
    backgroundColor: colors.state.errorBg,
  },
  legendItem: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 4,
  },
  legendPending: {
    backgroundColor: colors.state.warningBg,
  },
  legendPendingLeading: {
    backgroundColor: colors.state.successBg,
  },
  legendPendingTrailing: {
    backgroundColor: colors.state.errorBg,
  },
  legendPush: {
    backgroundColor: colors.neutral[200],
  },
  legendRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    justifyContent: 'center',
  },
  legendText: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 12,
    textTransform: 'uppercase',
  },
  memberCellDivider: {
    borderBottomColor: transparentColor,
    borderBottomWidth: 0,
  },
  metricCellButton: {
    alignItems: 'center',
    alignSelf: 'stretch',
    flex: 1,
    justifyContent: 'center',
    width: '100%',
  },
  metricCellButtonCompact: {
    alignItems: 'flex-start',
  },
  metricCellCompact: {
    alignItems: 'flex-start',
    paddingLeft: 0,
  },
  metricHeaderControl: {
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 32,
    paddingHorizontal: 2,
    paddingVertical: 4,
    width: '100%',
  },
  perfectWeekBadge: {
    alignItems: 'center',
    alignSelf: 'center',
    backgroundColor: colors.ink.acid,
    borderColor: colors.ink.press,
    borderRadius: 0,
    borderWidth: 2,
    flexDirection: 'row',
    gap: 8,
    justifyContent: 'center',
    marginTop: 12,
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  perfectWeekLabel: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 12,
    fontWeight: '600',
  },
  perfectWeekValue: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 12,
    fontWeight: 'bold',
  },
  pickCell: {
    alignItems: 'center',
    borderRadius: 8,
    justifyContent: 'center',
    minHeight: 30,
    paddingHorizontal: 5,
    paddingVertical: 4,
    width: '100%',
  },
  pickCellCorrect: {
    backgroundColor: colors.state.successBg,
  },
  pickCellEmpty: {
    backgroundColor: transparentColor,
  },
  pickCellHidden: {
    backgroundColor: transparentColor,
  },
  pickCellIncorrect: {
    backgroundColor: colors.state.errorBg,
  },
  pickCellPending: {
    backgroundColor: colors.state.warningBg,
  },
  pickCellPendingLeading: {
    backgroundColor: colors.state.successBg,
  },
  pickCellPendingTrailing: {
    backgroundColor: colors.state.errorBg,
  },
  pickCellPush: {
    backgroundColor: colors.neutral[200],
  },
  pickCellTiebreaker: {
    backgroundColor: colors.state.neutralBg,
  },
  pickContent: {
    alignItems: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 2,
    justifyContent: 'center',
    padding: 0,
  },
  pickTextCorrect: {
    color: colors.success[700],
    fontFamily: typography.fontFamily.mono,
    fontSize: 11,
    fontWeight: '600',
    textAlign: 'center',
  },
  pickTextEmpty: {
    color: colors.text.secondary,
    fontSize: 11,
    textAlign: 'center',
  },
  pickTextIncorrect: {
    color: colors.error[700],
    fontFamily: typography.fontFamily.mono,
    fontSize: 11,
    fontWeight: '600',
    textAlign: 'center',
  },
  pickTextPending: {
    color: colors.warning[700],
    fontFamily: typography.fontFamily.mono,
    fontSize: 11,
    fontWeight: '600',
    textAlign: 'center',
  },
  pickTextPendingLeading: {
    color: colors.success[700],
    fontSize: 11,
    fontWeight: '600',
    textAlign: 'center',
  },
  pickTextPendingTrailing: {
    color: colors.error[700],
    fontSize: 11,
    fontWeight: '600',
    textAlign: 'center',
  },
  pickTextPush: {
    color: colors.text.secondary,
    fontSize: 11,
    fontWeight: '600',
    textAlign: 'center',
  },
  pickTextTiebreaker: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 11,
    fontWeight: '600',
    textAlign: 'center',
  },
  playerNameLine: {
    lineHeight: 16,
    width: '100%',
  },
  playerText: {
    color: colors.text.primary,
    flexWrap: 'wrap',
    fontFamily: typography.fontFamily.body,
    fontSize: 14,
    fontWeight: '500',
  },
  playerTextCompact: {
    fontSize: 13,
    lineHeight: 16,
  },
  pointsHeaderButton: {
    gap: 2,
  },
  pointsHeaderPressed: {
    backgroundColor: colors.ink.acid,
  },
  pointsModeText: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 10,
  },
  pointsText: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 22,
    fontVariant: ['tabular-nums'],
    fontWeight: '700',
    lineHeight: 24,
  },
  rankText: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 11,
    fontVariant: ['tabular-nums'],
  },
  scoreText: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 9,
    fontWeight: '600',
    textAlign: 'center',
  },
  scoreTextCompact: {
    fontSize: 8,
    lineHeight: 10,
  },
  scrollDataRow: {
    borderBottomLeftRadius: 0,
    borderTopLeftRadius: 0,
  },
  scrollHeaderRow: {
    borderBottomLeftRadius: 0,
    borderTopLeftRadius: 0,
    marginBottom: 0,
  },
  scrollHeaderViewport: {
    overflow: 'hidden',
    width: '100%',
  },
  scrollableScoreBody: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
  },
  scrollableScoreContent: {
    paddingRight: 4,
  },
  scrollableScoreHeader: {
    flex: 1,
    minWidth: 0,
  },
  scrollableScorePane: {
    flex: 1,
    minWidth: 0,
    position: 'relative',
  },
  teamLogo: {
    height: 22,
    width: 22,
  },
  teamLogoCompact: {
    height: 18,
    width: 18,
  },
  teamLogoFallback: {
    alignItems: 'center',
    backgroundColor: colors.neutral[100],
    justifyContent: 'center',
  },
  teamLogoFallbackText: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 6,
    fontWeight: '700',
  },
  teamLogoWide: {
    height: 26,
    width: 26,
  },
  tooltipDescription: {
    color: colors.text.inverse,
    fontSize: 11,
    opacity: 0.82,
    textAlign: 'center',
  },
  tooltipTitle: {
    color: colors.text.inverse,
    fontSize: 12,
    fontWeight: '700',
    marginBottom: 2,
    textAlign: 'center',
  },
  transparentCard: {
    backgroundColor: transparentColor,
    borderWidth: 0,
    elevation: 0,
    shadowOpacity: 0,
  },
  viewModeButton: {
    borderRadius: 0,
    paddingHorizontal: 16,
    paddingVertical: 6,
  },
  viewModeButtonActive: {
    backgroundColor: colors.ink.acid,
    borderColor: colors.ink.press,
    borderWidth: 2,
    elevation: 2,
    shadowColor: colors.ink.press,
    shadowOffset: { width: 4, height: 4 },
    shadowOpacity: 1,
    shadowRadius: 0,
  },
  viewModeButtonText: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 12,
    fontWeight: '500',
  },
  viewModeButtonTextActive: {
    color: colors.text.primary,
    fontWeight: '700',
  },
  viewModeToggle: {
    alignSelf: 'center',
    backgroundColor: colors.ink.paperDark,
    borderColor: colors.ink.press,
    borderRadius: 0,
    borderWidth: 2,
    flexDirection: 'row',
    justifyContent: 'center',
    marginBottom: 12,
    padding: 2,
  },
  weekPickerHeaderCell: {
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingHorizontal: 6,
    paddingVertical: 4,
  },
  weekPickerHeaderCellCompact: {
    paddingHorizontal: 2,
  },
  weekPickerTrigger: {
    alignSelf: 'flex-start',
    backgroundColor: transparentColor,
    borderColor: transparentColor,
    borderWidth: 0,
    columnGap: 8,
    elevation: 0,
    height: 40,
    justifyContent: 'flex-start',
    maxWidth: '100%',
    paddingHorizontal: 0,
    shadowColor: transparentColor,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0,
    shadowRadius: 0,
  },
  weekPickerTriggerCompact: {
    columnGap: 4,
    height: 36,
  },
  weekPickerValue: {
    color: colors.ink.press,
    flex: 0,
    flexBasis: 'auto',
    flexGrow: 0,
    flexShrink: 1,
    fontFamily: typography.fontFamily.display,
    fontSize: 22,
    fontWeight: '900',
    letterSpacing: -0.4,
    lineHeight: 26,
  },
  weekPickerValueCompact: {
    fontSize: 18,
    lineHeight: 22,
  },
  wideDataRow: {
    borderBottomColor: colors.neutral[200],
    borderBottomWidth: 1,
    borderRadius: 0,
    marginBottom: 0,
  },
  widePlayerText: {
    fontSize: 14,
    lineHeight: 18,
  },
  wideRankText: {
    fontSize: 14,
  },
  winningPointsCell: {
    backgroundColor: colors.ink.acid,
    borderRadius: 0,
    justifyContent: 'center',
  },
  winningPointsText: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 10,
    fontWeight: '600',
    textAlign: 'center',
  },
  zebraCell: {
    backgroundColor: colors.neutral[300],
  },
  zebraRow: {
    backgroundColor: colors.neutral[300],
  },
}));
